import { sc, UI_SCALE } from '@/utils/uiScale';
import { LIVE_NOW_COLOR, RULER_COLOR } from '@/components/LiveNowLine';
/**
 * High-performance canvas renderer for roster heatmap.
 *
 * Replaces Plotly with custom canvas rendering for:
 * - 5-10ms partial updates (vs 1500-2000ms with Plotly)
 * - Native cell merging with lane-based overlap rendering
 * - Rich hover tooltips via HTML overlay
 * - Tiny bundle size (5KB vs 500KB)
 *
 * Performance optimizations:
 * - Only redraw changed cells
 * - Double buffering
 * - RequestAnimationFrame for smooth updates
 * - Efficient hit testing for hover
 *
 * Overlap rendering:
 * - When multiple officers share the same counter at overlapping slots,
 *   each officer's merged segment is rendered at a fractional cell height
 *   (1/N where N = max concurrent officers at any point in the segment).
 *   Lane assignment uses greedy interval coloring.
 */

import type { RosterGridData, CellData, HoverInfo, AvailabilityBracket, AvailabilityLine } from '@/types/api';
import { findOfficerBlock, type OfficerBlock } from '@/utils/blockMove';
import { DENSITY_PRESETS, type GridDensity } from '@/utils/gridDensity';
import { annotationFontSize, ellipsize, fitsStacked, fitTimes, layoutInline, noteFlag } from './segmentLayout';
import { layoutLaneTracks, needsNoteLane, noteLaneHeight } from './noteLane';

/** Zone-separator line color — the same purple as the header's ORCA/session-id background. */
const ZONE_SEPARATOR_COLOR = '#764ba2';

export interface RenderConfig {
  cellWidth: number;
  cellHeight: number;
  /** Optional per-row heights (px). When set, overrides uniform cellHeight for each row. */
  rowHeights?: number[];
  labelWidth: number;
  labelHeight: number;
  fontSize: number;
  fontFamily: string;
  borderColor: string;
  borderWidth: number;
  mergeConsecutive: boolean;
  /**
   * 'officer_meta' (default): label is derived from officer name/deployment.
   * 'cell_text': label is taken directly from cell.text (used for officer-schedule view).
   */
  textMode?: 'officer_meta' | 'cell_text';
  /** Row indices (0-based) at which to draw a thick horizontal separator line below that row. */
  zoneBoundaryRows?: number[];
  /**
   * Extra blank pixels inserted directly above a row, keyed by that row's index — the
   * room an expanded per-zone manning band (an HTML overlay) occupies. The band sits
   * just above the zone's boundary line, so a boundary row index is the usual key.
   * Rows below a gap shift down by it; the gap itself is not a clickable row.
   */
  zoneGaps?: Record<number, number>;
  /** Shrink availability-bracket chevrons to half their cell's width (counter graph only). */
  narrowChevrons?: boolean;
  /** Row-height floor and text size preset (see utils/gridDensity). Default 'fit'. */
  density?: GridDensity;
  /**
   * Give a note on a cell too narrow to show it a line of its own along the bottom of its row (see
   * canvas/noteLane). The rows that get one are taller by that line, which whoever sets the row
   * heights must allow for: `noteLaneLayout()` says which rows and how much, and `setNoteLane()`
   * tells the canvas what was reserved. `resizeToFit()` does both itself.
   */
  noteLane?: boolean;
}

/** What a grid's rows need for note lanes: lines under each row (absent = none), and one line's height. */
export interface NoteLaneLayout {
  tracks: Map<number, number>;
  lineH: number;
}

const DEFAULT_CONFIG: RenderConfig = {
  cellWidth: 40,
  cellHeight: 30,
  labelWidth: 30,
  labelHeight: 25,
  fontSize: 12,
  fontFamily: 'system-ui, -apple-system, sans-serif',
  borderColor: '#e0e0e0',
  borderWidth: 1,
  mergeConsecutive: true,
  textMode: 'officer_meta',
};

/** What drawSegmentText works out once and hands to the stacked/inline painters. */
interface SegmentTextStyle {
  annSize: number;
  labelSize: number;
  ink: string;
  timeFont: string;
  showTimes: boolean;
  showEndTime: boolean;
  startLabel: string;
  endLabel: string;
}

/** A resolved draw segment for one officer's consecutive run on one counter row. */
interface DrawSegment {
  officerId: string;
  counterRow: number;
  startCol: number;
  endCol: number;
  lane: number;       // 0-based vertical offset within the row
  totalLanes: number; // total lanes in the row at this segment (height divisor)
  color: string;
  text: string;
  /** 0-1, defaults to 1 — mirrors CellData.opacity (see there). A merged run is
   * one officer/counter throughout, so this is uniform across the segment;
   * taken from its first cell. */
  opacity?: number;
  /** Mirrors CellData.changed (see there). Not drawn: red text now means a call is still owed (see setPendingCalls). */
  changed?: boolean;
  /** Mirrors CellData.note (see there) — a caption drawn centered on the time-label
   * line, in a "tiktok caption" style highlight, when there's room for it. */
  note?: string;
  /** A non-counter deployment ("SOS DCAR"): drawn like a counter block, in gray, with its text allowed
   *  to be shortened to fit (a counter's label never is). */
  duty?: boolean;
  /** The half-height overlap piece of an officer whose full-height piece sits right beside it (an
   *  L-shaped cell): the full-height piece carries the annotations, this one repeats none. */
  continuation?: boolean;
  /** The columns of the whole merged shape this piece belongs to — itself, plus every same-officer,
   *  same-label piece joined to it edge to edge (an L-shaped cell is a full-height piece and a
   *  half-height one). Times and the hover card describe the shape, not the one piece. */
  spanStartCol: number;
  spanEndCol: number;
  suppressLeftBorder: boolean;  // true when same officer ends at startCol-1 on this row
  suppressRightBorder: boolean; // true when same officer starts at endCol+1 on this row
}

/** Shorten deployment to first 5 chars. */
function abbreviateDeployment(dep: string): string {
  return dep.trim().slice(0, 5);
}

/** Shorten name: first 3 chars of first word + 1st char of 2nd + 1st char of 3rd. */
function abbreviateName(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  let result = words[0].substring(0, 3);
  if (words.length >= 2) result += words[1][0];
  if (words.length >= 3) result += words[2][0];
  return result;
}

/** Fewest characters a cut-down label may keep before it is dropped instead.
 *  Counter labels run to four ("AC35"), so anything shorter is a fragment. */
const MIN_LABEL_CHARS = 4;

/** Smallest type a label may shrink to before the cell is left blank — below
 *  this it is decoration, not something anyone reads off a phone. */
export const MIN_LABEL_FONT_PX = 7;

/**
 * Smallest a counter row may be squeezed to: two text lines, the times above the name
 * (a note gets a line of its own below the row's cells, see noteLane). On a desktop-scaled
 * screen the floor and the font share of it, below, scale up so the cell labels match
 * the officer graph's sc(11) text.
 */
const MIN_ROW_H = UI_SCALE > 1 ? sc(24) : 22;

/** The row floor for a density preset (see utils/gridDensity). */
function minRowHeight(density: GridDensity = 'fit'): number {
  return Math.round(MIN_ROW_H * DENSITY_PRESETS[density].rowFloor);
}

/** Cell-label font for a row of the given height. */
function rowFontSize(cellH: number, density: GridDensity = 'fit'): number {
  const cap = DENSITY_PRESETS[density].fontCap;
  if (UI_SCALE > 1) return Math.max(sc(7), Math.min(Math.round(sc(12) * cap), Math.floor(cellH * 0.62)));
  return Math.max(7, Math.min(Math.round(12 * cap), Math.floor(cellH * 0.45)));
}

/**
 * Smallest size time and note text is drawn at. Below ~9 CSS px a standard
 * (1×) desktop monitor gives each glyph only five or six device pixels, and the
 * text renders as blocks — the "pixelated annotations" seen on office screens,
 * while the same text looked fine on a phone's 3× display.
 */
const ANNOTATION_MIN_PX = sc(9);

/** Greedy word wrap into at most maxLines lines, or null when a word or the whole won't fit. */
function wrapWords(text: string, maxLines: number, fits: (s: string) => boolean): string[] | null {
  const lines: string[] = [];
  let line = '';
  for (const word of text.split(/\s+/).filter(Boolean)) {
    const next = line ? `${line} ${word}` : word;
    if (fits(next)) { line = next; continue; }
    if (!line || lines.length + 1 >= maxLines) return null;
    lines.push(line);
    line = word;
    if (!fits(line)) return null;
  }
  if (line) lines.push(line);
  return lines.length > 1 ? lines : null;
}

export interface FittedLabel {
  /** One or more lines to stack. */
  lines: string[];
  /** The size they were fitted at — smaller than asked for on a narrow cell. */
  fontSize: number;
}

/**
 * Lay a cell label out in the room the cell actually has, or report that it
 * has none.
 *
 * A counter label is never shortened: "AC35" cut to "AC3" is not a shorter way
 * of writing the same counter, it is a different counter — and on a phone-width
 * cell that is the one the reader acts on. So the label is drawn whole: one
 * line, or split across two and shrunk as far as it stays readable, or not at
 * all. Officer names carry no such ambiguity and may still be cut — but never
 * below MIN_LABEL_CHARS characters, past which a fragment says nothing worth
 * the ink.
 *
 * Width is measured, never estimated from character counts, so the same code
 * settles the question on a phone, a tablet, and a desktop.
 *
 * Returns what to draw, or null to draw nothing.
 */
/** Whether two cells draw the same. */
function sameCell(a: CellData | undefined, b: CellData | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.color === b.color && a.text === b.text && a.unavailable === b.unavailable
    && a.counter === b.counter && a.opacity === b.opacity && a.changed === b.changed && a.note === b.note
    && a.duty === b.duty
    && a.officers.length === b.officers.length && a.officers.every((o, i) => o === b.officers[i])
    && a.colors.length === b.colors.length && a.colors.every((c, i) => c === b.colors[i]);
}

const sameList = (a: readonly string[], b: readonly string[]) =>
  a === b || (a.length === b.length && a.every((v, i) => v === b[i]));

/**
 * The rows whose drawing differs between two grids, or null when something besides the cells differs and the whole grid must be redrawn.
 * O(cells): a comparison per cell, against a full redraw's measuring and painting of
 * every one.
 */
export function changedRows(a: RosterGridData, b: RosterGridData): Set<number> | null {
  if (a === b) return new Set();
  if (a.num_counters !== b.num_counters || a.num_slots !== b.num_slots
    || !sameList(a.time_labels, b.time_labels) || !sameList(a.counter_labels, b.counter_labels)) return null;
  if (a.officer_metadata !== b.officer_metadata
    && JSON.stringify(a.officer_metadata) !== JSON.stringify(b.officer_metadata)) return null;
  if (a.assignment_notes !== b.assignment_notes
    && JSON.stringify(a.assignment_notes ?? null) !== JSON.stringify(b.assignment_notes ?? null)) return null;
  const changed = new Set<number>();
  for (let i = 0; i < b.cells.length; i++) {
    if (!sameCell(a.cells[i], b.cells[i])) changed.add(b.cells[i]?.row ?? Math.floor(i / b.num_slots));
  }
  const bracketsByRow = (g: RosterGridData) => {
    const m = new Map<number, string>();
    for (const br of g.availability_brackets ?? []) m.set(br.row, (m.get(br.row) ?? '') + JSON.stringify(br));
    return m;
  };
  const ba = bracketsByRow(a);
  const bb = bracketsByRow(b);
  for (const row of new Set([...ba.keys(), ...bb.keys()])) if (ba.get(row) !== bb.get(row)) changed.add(row);
  // A repaint of most of the grid is a full redraw anyway, without the clipping.
  if (changed.size * 3 > b.num_counters) return null;
  return changed;
}

export function fitCellLabel(
  text: string,
  opts: {
    maxWidth: number;
    maxHeight: number;
    fontSize: number;
    /** False for counter labels: better blank than a different counter's name. */
    mayTruncate: boolean;
    measure: (text: string, fontSize: number) => number;
  },
): FittedLabel | null {
  const { maxWidth, maxHeight, fontSize, mayTruncate, measure } = opts;
  if (!text || maxWidth <= 0 || maxHeight <= 0) return null;

  const fits = (s: string, size: number) => measure(s, size) <= maxWidth;

  if (fits(text, fontSize)) return { lines: [text], fontSize };

  // A single character has no shorter form and cannot be read as some other
  // counter, so it is shrunk but never dropped — and drawn even when the cell
  // is narrower than the glyph. The canvas clips it, and a clipped "#" still
  // reads as a break, where a blank cell reads as nothing at all.
  if (text.length === 1) {
    for (let size = fontSize - 1; size > MIN_LABEL_FONT_PX; size -= 1) {
      if (fits(text, size)) return { lines: [text], fontSize: size };
    }
    return { lines: [text], fontSize: MIN_LABEL_FONT_PX };
  }

  // An officer name is wrapped at word breaks, as many lines as the cell is tall
  // enough for, shrinking only a little. Splitting it mid-word and shrinking to
  // fit — what a counter label has to do — left long names unreadably small in
  // cells with plenty of room, so past the readable floor it is cut instead.
  if (mayTruncate) {
    const floor = Math.min(fontSize, Math.max(MIN_LABEL_FONT_PX, Math.ceil(fontSize * 0.75)));
    for (let size = fontSize; size >= floor; size -= 1) {
      const maxLines = Math.floor(maxHeight / (size * 1.15));
      if (maxLines < 2) break;
      const lines = wrapWords(text, maxLines, (s) => measure(s, size) <= maxWidth);
      if (lines) return { lines, fontSize: size };
    }
  }

  // Two lines, shrinking the type as far as it stays readable, while the cell
  // is tall enough to stack them. A narrow cell is short of width, not height.
  if (!mayTruncate && text.length >= 2) {
    const mid = Math.ceil(text.length / 2);
    const [top, bottom] = [text.slice(0, mid), text.slice(mid)];
    for (let size = fontSize; size >= MIN_LABEL_FONT_PX; size -= 1) {
      if (maxHeight < size * 2.1) continue;
      if (fits(top, size) && fits(bottom, size)) return { lines: [top, bottom], fontSize: size };
    }
  }

  if (!mayTruncate) return null;

  let shortened = text;
  while (shortened.length > MIN_LABEL_CHARS && !fits(shortened, fontSize)) {
    shortened = shortened.slice(0, -1);
  }
  return fits(shortened, fontSize) ? { lines: [shortened], fontSize } : null;
}

/**
 * Canvas-based roster renderer
 */
/**
 * Drag-to-move gesture for one officer's block of slots. Only vertical (counter
 * to counter) — the time range never changes — so the canvas reports raw pointer
 * positions and the row underneath, and the owner decides what a drop means.
 */
export interface BlockDragEvent {
  phase: 'start' | 'move' | 'end' | 'cancel';
  block: OfficerBlock;
  /** Row the block was picked up from. */
  sourceRow: number;
  /** Row under the pointer, or -1 when over a zone gap / outside the grid. */
  row: number;
  clientX: number;
  clientY: number;
}

export class RosterCanvas {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private config: RenderConfig;
  private gridData: RosterGridData | null = null;
  private drawSegments: DrawSegment[] = [];
  /** Lines of note lane reserved under each row, and the height of one line in px (see noteLaneLayout). */
  private laneTracks: Map<number, number> = new Map();
  private laneLineH = 0;
  // The gridData reference computeDrawSegments() last ran against. Segment layout
  // (which slots belong to which officer, lane assignment) depends only on the DATA,
  // never on pixel geometry — but a resize (side panel opening/closing, split-divider
  // drag) calls render(this.gridData) with the SAME reference on every frame of the
  // CSS transition purely to reflow pixel positions. Recomputing segments from scratch
  // on each of those frames was pure waste and, being synchronous and non-trivial
  // (sorting + lane assignment over every officer), was exactly what made scrolling
  // stutter while a panel was animating open/closed. Skipped whenever the reference
  // is unchanged; a real data update always passes a new object (see
  // RosterGrid.tsx's gridDataWithBrackets memo), so this can't go stale.
  private segmentsComputedFor: RosterGridData | null = null;
  /** Measured text widths by font and text (see textWidth). */
  private widthCache = new Map<string, number>();
  /** The context's font as last set through setFont — reading ctx.font back is slow;
   *  null after a restore() may have changed it. */
  private font: string | null = null;
  private hoverCallback?: (info: HoverInfo | null) => void;
  /** What emitHover last reported, so an unchanged hover is not reported again. */
  private lastHoverKey = '';
  private lastHoverData: RosterGridData | null = null;
  private clickCallback?: (info: HoverInfo) => void;
  private onDragSelectCallback?: (row: number, startCol: number, endCol: number, clientX: number, clientY: number, mode: 'add' | 'erase', gesture: 'click' | 'drag') => void;
  private dragState: { row: number; startCol: number; curCol: number; startCellType: 'counter' | 'break' | 'empty'; longPressFired?: boolean; blockRuledOut?: boolean } | null = null;

  // ── Mouse long-press (every row unless narrowed via setLongPressRowPredicate) ──
  // A mouse has no built-in equivalent of a stationary touch long-press: holding
  // the button down without moving to another column normally just resolves to
  // a 'click' gesture on mouseup. All rows get a timer here (a predicate can
  // narrow that) so press-and-hold to add an officer/break works with a mouse
  // exactly as it does on touch. The timer fires a 'drag' gesture on the SAME cell if the
  // button is still down and hasn't moved to another column — mirroring the
  // touch state machine's 'pending' -> 'selecting' transition.
  private mouseLongPressTimer: ReturnType<typeof setTimeout> | null = null;
  private longPressRowPredicate: ((row: number) => boolean) | null = null;
  private readonly MOUSE_LONG_PRESS_MS = 400;

  // ── Touch long-press state machine ────────────────────────────────────────
  // 'idle'      — no touch active
  // 'pending'   — touch held, timer running, horizontal movement not yet exceeded threshold
  // 'scrolling' — threshold exceeded before 350ms, browser handles scroll
  // 'selecting' — 350ms elapsed, user is dragging to select a range
  // 'blockDragging' — a long-press on an officer block turned into a vertical drag-to-move
  private touchState: 'idle' | 'pending' | 'scrolling' | 'selecting' | 'blockDragging' = 'idle';
  private touchStart: { clientX: number; clientY: number; row: number; col: number; startCellType: 'counter' | 'break' | 'empty' } | null = null;
  private touchCurCol = 0;
  /** Set once a held touch first moves sideways (or can't be picked up) — no block move for this press. */
  private touchBlockRuledOut = false;
  private touchLongPressTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly LONG_PRESS_MS = 350;
  private readonly SCROLL_THRESHOLD_PX = 8;

  // ── Drag-to-move of an officer block ───────────────────────────────────────
  private blockDragHandler: ((e: BlockDragEvent) => void) | null = null;
  private blockDragEnabled = false;
  private blockDrag: { block: OfficerBlock; sourceRow: number; lastX: number; lastY: number } | null = null;
  private mouseDownPt: { x: number; y: number } | null = null;
  /** Movement (px) before a press counts as a drag rather than a click. */
  private readonly BLOCK_DRAG_THRESHOLD_PX = 8;

  private animationFrameId?: number;
  /** Logical (CSS) pixel dimensions — used for clearRect and re-render after DPR change. */
  private logicalWidth = 0;
  private logicalHeight = 0;
  /** Cleanup function for the current DPR media-query listener. */
  private dprCleanup?: () => void;
  private highlightedOfficerIds: Set<string> | null = null;
  /** Extra left inset (px) for a segment's text, keyed `officerId|row|startCol` — the room a phone icon pinned to its corner takes. */
  private textInsets = new Map<string, number>();
  /** Room kept free at a segment's right edge (the NR button), keyed like textInsets. */
  private rightInsets = new Map<string, number>();
  /** Availability windows of the highlighted officers, drawn as full-height lines over the dimmed grid. */
  private availabilityLines: AvailabilityLine[] = [];

  // ── Live cell presence (other users currently editing) ─────────────────────
  private presenceEntries: Array<{ row: number; col: number; officerKey?: string; color: string; userName: string }> = [];
  /** Hit areas for presence avatar bubbles, recomputed every drawPresenceOverlay() call. */
  private presenceBubbleHitboxes: Array<{ x: number; y: number; r: number; row: number; userName: string }> = [];
  private presenceBubbleClickCallback?: (userName: string, clientX: number, clientY: number) => void;

  constructor(
    canvas: HTMLCanvasElement,
    config: Partial<RenderConfig> = {},
    onHover?: (info: HoverInfo | null) => void,
    onClick?: (info: HoverInfo) => void,
    onDragSelect?: (row: number, startCol: number, endCol: number, clientX: number, clientY: number, mode: 'add' | 'erase', gesture: 'click' | 'drag') => void,
    onPresenceBubbleClick?: (userName: string, clientX: number, clientY: number) => void,
  ) {
    this.canvas = canvas;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.hoverCallback = onHover;
    this.clickCallback = onClick;
    this.onDragSelectCallback = onDragSelect;
    this.presenceBubbleClickCallback = onPresenceBubbleClick;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2D context');
    }
    this.ctx = ctx;

    // Set up mouse events — mousedown/mouseup to distinguish click vs drag
    this.canvas.addEventListener('mousemove', this.handleMouseMove);
    this.canvas.addEventListener('mouseleave', this.handleMouseLeave);
    this.canvas.addEventListener('mousedown', this.handleMouseDown);

    // Set up touch events — long-press to start selection, short swipe = scroll
    // touchstart is passive so iOS can commit to scroll on the parent container immediately
    // without waiting for JS (handleTouchStart never calls preventDefault).
    this.canvas.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.canvas.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    this.canvas.addEventListener('touchend', this.handleTouchEnd, { passive: false });
    this.canvas.addEventListener('touchcancel', this.handleTouchCancel, { passive: false });

    // Listen for browser zoom changes (DPR changes) and re-render
    this.setupDprListener();
  }

  // Slot index of the live "NOW" time, or null. Painted inside the canvas, after
  // the empty cells and gridlines but before the officer segments, so the line
  // shows only on empty cells and sits behind every assignment and its text.
  private liveNowSlot: number | null = null;

  private liveNowPinned = false;

  setLiveNowSlot(slot: number | null, pinned = false): void {
    if (slot === this.liveNowSlot && pinned === this.liveNowPinned) return;
    this.liveNowSlot = slot;
    this.liveNowPinned = pinned;
    if (this.gridData) this.render(this.gridData);
  }

  private drawLiveNowLine(): void {
    if (this.liveNowSlot == null) return;
    const x = this.config.labelWidth + this.liveNowSlot * this.config.cellWidth;
    const top = this.config.labelHeight ?? 0;
    this.ctx.save();
    this.ctx.fillStyle = this.liveNowPinned ? RULER_COLOR : LIVE_NOW_COLOR;
    this.ctx.fillRect(x - 1, top, 2, this.totalGridH());
    this.restoreCtx();
  }

  /** Told after every render(), so HTML overlays pinned to blocks (getSegmentRects) can follow the layout. */
  private afterRenderCallback: (() => void) | null = null;
  setAfterRender(callback: (() => void) | null): void {
    this.afterRenderCallback = callback;
  }

  /**
   * Render complete grid
   */
  render(gridData: RosterGridData): void {
    this.renderGrid(gridData);
    // Any redraw (the live-now line moving, a hover, a presence change) clears the edit traces
    // off the canvas, so the ones still wanted go straight back on rather than waiting for the
    // owner to ask again — otherwise they flicker out and later pop back up on their own.
    if (this.activeTraces.length > 0) this.overlayEditTraces(this.activeTraces);
    this.afterRenderCallback?.();
  }

  private renderGrid(gridData: RosterGridData): void {
    this.renderGen++;
    this.gridData = gridData;
    if (!this.config.mergeConsecutive) {
      this.drawSegments = [];
      this.segmentsComputedFor = null;
    } else if (gridData !== this.segmentsComputedFor) {
      this.drawSegments = this.computeDrawSegments(gridData);
      this.segmentsComputedFor = gridData;
    }
    // else: same data as last time (a pure resize) — this.drawSegments is still valid.

    // Resize canvas
    this.resizeCanvas(gridData);

    // An edit changes a few rows: when nothing but those rows' cells differs from what
    // is on the canvas, only they are repainted (see changedRows).
    const drawnKey = this.drawnKey();
    const rows = this.lastDrawn && this.lastDrawn.key === drawnKey
      ? changedRows(this.lastDrawn.gridData, gridData)
      : null;
    this.lastDrawn = { gridData, key: drawnKey };
    this.repainted = null;
    if (rows) {
      if (rows.size > 0) this.repaintRows(gridData, rows);
      else this.repainted = { rows, strips: [] };
      return;
    }
    this.tracesKey = '';   // a full redraw wipes the edit traces too

    // Clear canvas (use logical dimensions — DPR transform is already applied)
    this.ctx.clearRect(0, 0, this.logicalWidth, this.logicalHeight);

    // Draw components
    if (this.config.labelHeight > 0) this.drawTimeLabels(gridData.time_labels);
    if (this.config.labelWidth > 0) this.drawCounterLabels(gridData.counter_labels);
    this.drawGrid(gridData);
    this.drawZoneSeparators();
    this.applyHighlightOverlay();
  }

  /** What was last drawn, to tell an edit's few changed rows from a full redraw. */
  private lastDrawn: { gridData: RosterGridData; key: string } | null = null;
  /** The edit traces on the canvas (see overlayEditTraces); '' when none. */
  private tracesKey = '';
  /** The traces wanted on the canvas right now, redrawn by every render(). */
  private activeTraces: Array<{ row: number; col: number; color: string; traceType?: 'add' | 'delete' | 'change' }> = [];
  /** Counts renderGrid() passes; with tracesGen it tells whether this pass already carries the traces. */
  private renderGen = 0;
  private tracesGen = -1;
  /** What the last render() repainted — null when it drew the whole grid: the rows
   *  drawn, and the strips (y, height) the drawing was clipped to. */
  private repainted: { rows: Set<number>; strips: Array<[number, number]> } | null = null;
  /** While repainting some rows: the per-row drawing passes skip every other row. */
  private rowFilter: Set<number> | null = null;

  /** Everything besides the cells that a drawn grid depends on: when any of it changes,
   *  the whole grid is redrawn. */
  private drawnKey(): string {
    const c = this.config;
    return JSON.stringify([
      this.canvas.width, this.canvas.height, c.cellWidth, c.cellHeight, c.rowHeights, c.labelWidth, c.labelHeight,
      c.fontSize, c.density, c.textMode, c.zoneGaps, c.zoneBoundaryRows, this.liveNowSlot, this.liveNowPinned,
      this.highlightedOfficerIds ? [...this.highlightedOfficerIds] : null, this.availabilityLines, [...this.textInsets], [...this.rightInsets], this.pendingCalls, this.presenceEntries,
    ]);
  }

  /**
   * Repaint just `changed` rows, pixel for pixel as a full redraw would: clear their
   * strips (and a few pixels past each edge, where borders and lines straddle the row
   * boundary), and replay the same drawing passes clipped to that — the per-row passes
   * for those rows and their neighbours (whatever paints into the margin), the rest
   * (gridlines, separators, live-now line) whole. Nothing outside the strips changes.
   */
  private repaintRows(gridData: RosterGridData, changed: Set<number>): void {
    const MARGIN = 4;
    const width = this.logicalWidth;
    const rows = new Set<number>();
    for (const row of changed) {
      for (const r of [row - 1, row, row + 1]) if (r >= 0 && r < gridData.num_counters) rows.add(r);
    }
    const strips: Array<[number, number]> = [...changed].map((row) => {
      const top = Math.max(0, this.rowY(row) - MARGIN);
      return [top, Math.min(this.logicalHeight, this.rowY(row) + this.rowH(row) + MARGIN) - top];
    });
    this.repainted = { rows, strips };
    this.ctx.save();
    this.clipToStrips(strips);
    for (const [y, h] of strips) this.ctx.clearRect(0, y, width, h);
    this.rowFilter = rows;
    try {
      this.drawGrid(gridData);
      this.drawZoneSeparators();
      this.applyHighlightOverlay();
    } finally {
      this.rowFilter = null;
      this.restoreCtx();
    }
  }

  /** Something was painted over the grid outside render() (a selection tint, a pending
   *  edit): the canvas no longer shows what was last drawn, so the next render is full. */
  private paintedOutsideRender(): void {
    this.lastDrawn = null;
  }

  private clipToStrips(strips: Array<[number, number]>): void {
    this.ctx.beginPath();
    for (const [y, h] of strips) this.ctx.rect(0, y, this.logicalWidth, h);
    this.ctx.clip();
  }

  private inRows(row: number): boolean {
    return !this.rowFilter || this.rowFilter.has(row);
  }

  /**
 * Paint a pending-edit overlay on top of the already-rendered grid.
 * Called after every anchor toggle so the user sees immediate feedback.
 *
 * pendingSlots: array indexed by col. Each entry is null (empty),
 *   { type: 'break' }, or { type: 'counter', counter_label, officer_id }
 * rowIndex: which canvas row to paint (counter row index or officer row index)
 */
renderOverlay(
  pendingSlots: Array<{ type: 'counter' | 'break'; counter_label?: string; officer_id?: string } | null>,
  rowIndex: number,
): void {
  if (!this.gridData) return;
  this.paintedOutsideRender();

  const { labelWidth, cellWidth, fontSize, fontFamily } = this.config;
  const numSlots = pendingSlots.length;

  // Repaint only the target row from scratch (restore from gridData first)
  const y = this.rowY(rowIndex);
  const rh = this.rowH(rowIndex);
  const cellHeight = rh;

  // Clear the row
  this.ctx.clearRect(labelWidth, y, numSlots * cellWidth, rh);

  // Redraw original cells for this row
  for (let col = 0; col < this.gridData.num_slots; col++) {
    const cell = this.gridData.cells[rowIndex * this.gridData.num_slots + col];
    if (cell.officers.length === 0 || cell.unavailable) {
      this.drawCell(cell);
    }
  }
  // Redraw original segments for this row
  for (const seg of this.drawSegments) {
    if (seg.counterRow === rowIndex) this.drawSegment(seg);
  }
  // Note captions for this row's unassigned cells — after their backgrounds, same
  // reason as the full-grid pass (see drawUnassignedNoteCaptions).
  this.drawUnassignedNoteCaptions(this.gridData, rowIndex);

  // Restore hourly gridlines and zone separators erased by the row clearRect
  this.redrawRowOverlays(rowIndex);

  // Now paint pending overlay on top
  for (let col = 0; col < numSlots; col++) {
    const slot = pendingSlots[col];
    if (!slot) continue;

    const x = labelWidth + col * cellWidth;
    const prev = col > 0 ? pendingSlots[col - 1] : null;
    const next = col < numSlots - 1 ? pendingSlots[col + 1] : null;

    if (slot.type === 'break') {
      // Break: green tint
      this.ctx.fillStyle = '#EAF3DE';
      this.ctx.fillRect(x, y, cellWidth, cellHeight);
      this.ctx.strokeStyle = '#e0e0e0';
      this.ctx.lineWidth = 1;
      this.ctx.strokeRect(x, y, cellWidth, cellHeight);
      this.ctx.fillStyle = '#3B6D11';
      this.setFont(`bold ${fontSize}px ${fontFamily}`);
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';
      this.ctx.fillText('#', x + cellWidth / 2, y + cellHeight / 2);

    } else {
      // Counter/officer assignment: blue tint with run border logic
      const key = slot.officer_id ?? slot.counter_label ?? '';
      const prevKey = prev?.type === 'counter' ? (prev.counter_label ?? prev.officer_id ?? '') : null;
      const nextKey = next?.type === 'counter' ? (next.counter_label ?? next.officer_id ?? '') : null;
      const sameLeft = prevKey === key;
      const sameRight = nextKey === key;

      this.ctx.fillStyle = '#E6F1FB';
      this.ctx.fillRect(x, y, cellWidth, cellHeight);

      // Run borders — thick blue like the mockup
      this.ctx.strokeStyle = '#185FA5';
      this.ctx.lineWidth = 1.5;
      this.ctx.beginPath();
      // top
      this.ctx.moveTo(x, y); this.ctx.lineTo(x + cellWidth, y);
      // bottom
      this.ctx.moveTo(x, y + cellHeight); this.ctx.lineTo(x + cellWidth, y + cellHeight);
      this.ctx.stroke();

      // left border (thick, only at run start)
      if (!sameLeft) {
        this.ctx.lineWidth = 2;
        this.ctx.beginPath();
        this.ctx.moveTo(x, y); this.ctx.lineTo(x, y + cellHeight);
        this.ctx.stroke();
      }
      // right border (thick, only at run end)
      if (!sameRight) {
        this.ctx.lineWidth = 2;
        this.ctx.beginPath();
        this.ctx.moveTo(x + cellWidth, y); this.ctx.lineTo(x + cellWidth, y + cellHeight);
        this.ctx.stroke();
      }

      // Label only at run start
      if (!sameLeft && key) {
        this.ctx.fillStyle = '#0C447C';
        this.setFont(`bold ${fontSize}px ${fontFamily}`);
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.save();
        this.ctx.beginPath();
        this.ctx.rect(x, y, cellWidth * (sameRight ? 1 : 1), cellHeight);
        this.ctx.clip();
        this.ctx.fillText(key, x + cellWidth / 2, y + cellHeight / 2);
        this.restoreCtx();
      }
    }
  }
}
  /** Rows that must be at least this tall (px) — the room a phone icon needs; applied by the next resizeToFit(). */
  private rowMinHeights = new Map<number, number>();
  setRowMinHeights(mins: Map<number, number>): void {
    this.rowMinHeights = mins;
  }

  /** Set per-row heights; next render() call will use them. */
  setRowHeights(heights: number[]): void {
    this.config.rowHeights = heights;
  }

  /**
   * The note lane each row needs at this cell width: how many lines (0 = none) and how tall one
   * is — what to add to those rows' heights. Reads nothing it changes: the caller adds the height,
   * then hands the same layout back through setNoteLane(). Empty unless `config.noteLane` is on.
   */
  noteLaneLayout(
    gridData: RosterGridData | null = this.gridData,
    cellWidth: number = this.config.cellWidth,
  ): NoteLaneLayout {
    const tracks = new Map<number, number>();
    if (!this.config.noteLane || !this.config.mergeConsecutive || !gridData) return { tracks, lineH: 0 };
    const segments = gridData === this.segmentsComputedFor ? this.drawSegments : this.computeDrawSegments(gridData);
    const annSize = annotationFontSize(this.config.fontSize, ANNOTATION_MIN_PX);
    this.setFont(`bold ${annSize}px ${this.config.fontFamily}`);
    const rowRight = this.config.labelWidth + gridData.num_slots * cellWidth;
    const byRow = new Map<number, DrawSegment[]>();
    for (const seg of segments) {
      if (seg.continuation || !needsNoteLane(seg.note)) continue;
      (byRow.get(seg.counterRow) ?? byRow.set(seg.counterRow, []).get(seg.counterRow)!).push(seg);
    }
    for (const [row, segs] of byRow) {
      segs.sort((a, b) => a.startCol - b.startCol);
      const laid = layoutLaneTracks(
        segs.map((o) => ({ x: this.config.labelWidth + o.startCol * cellWidth, w: (o.endCol - o.startCol + 1) * cellWidth, note: o.note ?? '' })),
        rowRight, (t) => this.textWidth(t), { rowLeft: this.config.labelWidth },
      );
      if (laid.tracks > 0) tracks.set(row, laid.tracks);
    }
    return { tracks, lineH: tracks.size > 0 ? noteLaneHeight(annSize) : 0 };
  }

  /**
   * How much taller a row of height `baseH` has to be to hold `tracks` note-lane lines under its
   * blocks. Only the part the row has no spare room for: a block needs its times + label (stacked)
   * or just its label (a row too short to stack), and whatever height is left over beyond that
   * already holds the note, so such a row keeps its height and its blocks keep their layout.
   */
  noteLaneExtra(baseH: number, tracks: number, lineH: number, labelSize: number = this.config.fontSize): number {
    if (tracks <= 0 || lineH <= 0) return 0;
    const annSize = annotationFontSize(labelSize, ANNOTATION_MIN_PX);
    const stackedNeed = annSize + 2 + labelSize;
    const need = baseH >= stackedNeed ? stackedNeed : labelSize + 2;
    return Math.max(0, Math.ceil(tracks * lineH - Math.max(0, baseH - need)));
  }

  /** Record the lanes the row heights now allow for (from noteLaneLayout); the next render() draws them. */
  setNoteLane(layout: NoteLaneLayout): void {
    this.laneTracks = layout.tracks;
    this.laneLineH = layout.lineH;
  }

  /** Replace the zone gaps; next render() call will use them. */
  setZoneGaps(gaps: Record<number, number>): void {
    this.config.zoneGaps = gaps;
  }

  /** Total gap pixels sitting above `row` (gaps keyed at or before it). */
  private gapAbove(row: number): number {
    const gaps = this.config.zoneGaps;
    if (!gaps) return 0;
    let g = 0;
    for (const key of Object.keys(gaps)) {
      if (Number(key) <= row) g += gaps[Number(key)];
    }
    return g;
  }

  /** Total gap pixels across the whole grid, including any below the last row. */
  private totalGaps(): number {
    const gaps = this.config.zoneGaps;
    if (!gaps) return 0;
    let g = 0;
    for (const key of Object.keys(gaps)) g += gaps[Number(key)];
    return g;
  }

  /** Canvas Y coordinate of the top of the given row. */
  private rowY(row: number): number {
    const { labelHeight, cellHeight, rowHeights } = this.config;
    const gap = this.gapAbove(row);
    if (!rowHeights || rowHeights.length === 0) return labelHeight + row * cellHeight + gap;
    let y = labelHeight + gap;
    for (let i = 0; i < row; i++) y += rowHeights[i] ?? cellHeight;
    return y;
  }

  /** Pixel height of the given row (per-row override or uniform cellHeight). */
  private rowH(row: number): number {
    return this.config.rowHeights?.[row] ?? this.config.cellHeight;
  }

  /** Row index from a canvas-space Y coordinate, or -1 when it falls in a zone gap. */
  private rowFromY(canvasY: number): number {
    const { labelHeight, cellHeight, rowHeights } = this.config;
    const n = this.gridData?.num_counters ?? rowHeights?.length ?? 0;
    let y = labelHeight;
    for (let row = 0; row < n; row++) {
      const gap = this.config.zoneGaps?.[row] ?? 0;
      if (gap > 0) {
        y += gap;
        if (canvasY < y) return -1;
      }
      y += rowHeights?.length ? (rowHeights[row] ?? cellHeight) : cellHeight;
      if (canvasY < y) return row;
    }
    const tailGap = this.config.zoneGaps?.[n] ?? 0;
    return tailGap > 0 && canvasY < y + tailGap ? -1 : n - 1;
  }

  /** Total pixel height of all rows plus any zone gaps. */
  private totalGridH(): number {
    const n = this.gridData?.num_counters ?? 0;
    const { cellHeight, rowHeights } = this.config;
    const gaps = this.totalGaps();
    if (!rowHeights || rowHeights.length === 0) return n * cellHeight + gaps;
    let h = gaps;
    for (let i = 0; i < n; i++) h += rowHeights[i] ?? cellHeight;
    return h;
  }

  /**
   * Resize canvas cells to exactly fill the given container dimensions, then re-render.
   *
   * Used by ResizeObserver in grid components to make the canvas responsive.
   * When gridData is not yet loaded this is a no-op (will be called again after render).
   */
  resizeToFit(containerWidth: number, containerHeight: number, maxCellHeight?: number): void {
    if (!this.gridData) return;

    const { labelWidth, labelHeight } = this.config;
    const numSlots = this.gridData.num_slots;
    const numRows  = this.gridData.num_counters;

    const cellW = Math.max(9, this.minCellWidth(), (containerWidth - labelWidth) / numSlots);
    // A row holding a note is taller only where it has no spare room for the note's line (see
    // noteLaneExtra); that comes off the room the rows share, so the grid still fills the same height.
    const lane = this.noteLaneLayout(this.gridData, cellW);
    const floorH = minRowHeight(this.config.density);
    const extraFor = (h: number, row: number, labelSize: number) =>
      this.noteLaneExtra(h, lane.tracks.get(row) ?? 0, lane.lineH, labelSize);
    const gaps = this.totalGaps();
    const evenH = Math.max(floorH, (containerHeight - labelHeight - gaps) / numRows);
    let laneTotal = 0;
    for (const row of lane.tracks.keys()) laneTotal += extraFor(evenH, row, rowFontSize(evenH, this.config.density));
    let   cellH = Math.max(floorH, (containerHeight - labelHeight - gaps - laneTotal) / numRows);
    if (maxCellHeight !== undefined) cellH = Math.min(cellH, maxCellHeight);

    this.config.cellWidth  = cellW;
    this.config.cellHeight = cellH;
    // Row boundaries snapped to whole CSS pixels. cellH is almost always
    // fractional (container height / row count), and the HTML label column
    // beside the canvas stacks one box per row: with fractional boxes the two
    // round independently, so the further down the grid you look the further a
    // row header drifts from its own cells — the misalignment reported on iOS,
    // which rounds every box to device pixels. Rounding the CUMULATIVE offset
    // (not each height) keeps every boundary on an integer while the rows still
    // add up to the same total, so no space is lost at the bottom.
    this.config.rowHeights = Array.from(
      { length: numRows },
      (_, i) => {
        const base = Math.max(Math.round((i + 1) * cellH) - Math.round(i * cellH), this.rowMinHeights.get(i) ?? 0);
        return base + extraFor(base, i, rowFontSize(cellH, this.config.density));
      },
    );
    this.setNoteLane(lane);
    this.config.fontSize   = rowFontSize(cellH, this.config.density);

    this.render(this.gridData);
  }

  private setFont(font: string): void {
    this.ctx.font = font;
    this.font = font;
  }

  private restoreCtx(): void {
    this.ctx.restore();
    this.font = null;
  }

  /**
   * The width of `text` in the context's current font. Every redraw fits every block's
   * label by measuring it — thousands of measureText calls for one roster — and the
   * same names and times come round each time, so the widths are remembered.
   */
  private textWidth(text: string): number {
    const font = this.font ?? (this.font = this.ctx.font);
    const key = `${font}\u0000${text}`;
    let w = this.widthCache.get(key);
    if (w === undefined) {
      if (this.widthCache.size > 20_000) this.widthCache.clear();
      w = this.ctx.measureText(text).width;
      this.widthCache.set(key, w);
    }
    return w;
  }

  /**
   * Take new data without drawing it — for a caller about to resize, which draws.
   * Drawing here as well painted the whole grid twice for every roster change.
   */
  setData(gridData: RosterGridData): void {
    this.gridData = gridData;
  }

  /** Switch density preset; the caller re-runs resizeToFit / resizeWidth for the new sizes. */
  setDensity(density: GridDensity): void {
    this.config.density = density;
  }

  /** Base row height used for rows with no explicit height (the officer grid scales it with density). */
  setCellHeight(height: number): void {
    this.config.cellHeight = height;
  }

  /** Narrowest a column may be for the current density; below it the grid scrolls sideways. */
  private minCellWidth(): number {
    return Math.round(DENSITY_PRESETS[this.config.density ?? 'fit'].minCellWidth * (UI_SCALE > 1 ? Math.min(UI_SCALE, 1.4) : 1));
  }

  /**
   * Resize only the width dimension; height (cell height) stays fixed.
   * Used for the officer schedule where row count is dynamic.
   */
  resizeWidth(containerWidth: number): void {
    if (!this.gridData) return;

    const { labelWidth } = this.config;
    const numSlots = this.gridData.num_slots;

    this.config.cellWidth = Math.max(9, this.minCellWidth(), (containerWidth - labelWidth) / numSlots);
    this.config.fontSize  = rowFontSize(this.config.cellHeight, this.config.density);

    this.render(this.gridData);
  }

  /**
   * Partial update - only redraw changed cells
   */
  partialUpdate(changedCells: CellData[]): void {
    if (!this.gridData) return;
    this.paintedOutsideRender();

    // Cancel pending animation frame
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    // Schedule redraw
    this.animationFrameId = requestAnimationFrame(() => {
      for (const cell of changedCells) {
        this.drawCell(cell);
      }
      this.animationFrameId = undefined;
    });
  }

  /**
   * Compute draw segments for all officer assignments.
   *
   * For each counter row:
   * 1. Group each officer's consecutive slots into coarse segments.
   * 2. Count concurrent officers per slot.
   * 3. Split each coarse segment at boundaries where concurrency changes,
   *    so that non-overlap portions get totalLanes=1 (full height) and
   *    overlap portions get totalLanes=N (fractional height).
   * 4. Assign lanes using greedy interval coloring.
   */
  private computeDrawSegments(gridData: RosterGridData): DrawSegment[] {
    const allSegments: DrawSegment[] = [];
    const usesCellText = this.config.textMode === 'cell_text';

    for (let row = 0; row < gridData.num_counters; row++) {
      // Step 1: collect {officerId, slots[]} per officer for this row
      const officerSlots = new Map<string, number[]>();

      for (let col = 0; col < gridData.num_slots; col++) {
        const cell = gridData.cells[row * gridData.num_slots + col];
        for (const officerId of cell.officers) {
          if (!officerSlots.has(officerId)) officerSlots.set(officerId, []);
          officerSlots.get(officerId)!.push(col);
        }
      }

      // Step 2: collapse consecutive slots into coarse segments.
      // In cell_text mode (officer-schedule view) also break segments when the cell text
      // changes — prevents different counter labels from merging into one visual block.
      type RawSeg = { officerId: string; startCol: number; endCol: number; lane: number; totalLanes: number };
      const coarseSegs: RawSeg[] = [];

      for (const [officerId, slots] of officerSlots) {
        slots.sort((a, b) => a - b);
        let segStart = slots[0];
        let segEnd = slots[0];
        let segText = usesCellText
          ? (gridData.cells[row * gridData.num_slots + slots[0]]?.text ?? '')
          : null;
        let segNote = gridData.cells[row * gridData.num_slots + slots[0]]?.note;

        for (let i = 1; i < slots.length; i++) {
          const curText = usesCellText
            ? (gridData.cells[row * gridData.num_slots + slots[i]]?.text ?? '')
            : null;
          const curNote = gridData.cells[row * gridData.num_slots + slots[i]]?.note;
          const textChanged = usesCellText && curText !== segText;
          // Same idea for notes: a run whose note changes partway through must not
          // silently merge into one segment showing only the first note.
          const noteBoundary = curNote !== segNote;

          if (slots[i] === segEnd + 1 && !textChanged && !noteBoundary) {
            segEnd = slots[i];
          } else {
            coarseSegs.push({ officerId, startCol: segStart, endCol: segEnd, lane: 0, totalLanes: 1 });
            segStart = slots[i];
            segEnd = slots[i];
            segText = curText;
            segNote = curNote;
          }
        }
        coarseSegs.push({ officerId, startCol: segStart, endCol: segEnd, lane: 0, totalLanes: 1 });
      }

      if (coarseSegs.length === 0) continue;

      // Step 3: compute concurrent count per slot
      const concurrentAtSlot = new Array<number>(gridData.num_slots).fill(0);
      for (const seg of coarseSegs) {
        for (let c = seg.startCol; c <= seg.endCol; c++) {
          concurrentAtSlot[c]++;
        }
      }

      // Step 4: split each coarse segment at boundaries where concurrentAtSlot changes
      const rowSegs: RawSeg[] = [];
      for (const seg of coarseSegs) {
        let subStart = seg.startCol;
        for (let c = seg.startCol + 1; c <= seg.endCol; c++) {
          if (concurrentAtSlot[c] !== concurrentAtSlot[subStart]) {
            rowSegs.push({
              officerId: seg.officerId,
              startCol: subStart,
              endCol: c - 1,
              lane: 0,
              totalLanes: concurrentAtSlot[subStart],
            });
            subStart = c;
          }
        }
        rowSegs.push({
          officerId: seg.officerId,
          startCol: subStart,
          endCol: seg.endCol,
          lane: 0,
          totalLanes: concurrentAtSlot[subStart],
        });
      }

      // Step 5: greedy interval coloring — assign lanes
      rowSegs.sort((a, b) => a.startCol - b.startCol);
      const laneEnd: number[] = [];

      for (const seg of rowSegs) {
        let assignedLane = -1;
        for (let lane = 0; lane < laneEnd.length; lane++) {
          if (laneEnd[lane] < seg.startCol) {
            assignedLane = lane;
            break;
          }
        }
        if (assignedLane === -1) {
          assignedLane = laneEnd.length;
          laneEnd.push(-1);
        }
        laneEnd[assignedLane] = seg.endCol;
        seg.lane = assignedLane;
      }

      // Step 6: resolve color and text from cell data, emit DrawSegments
      for (const seg of rowSegs) {
        const startCell = gridData.cells[row * gridData.num_slots + seg.startCol];
        const officerIdx = startCell.officers.indexOf(seg.officerId);
        const color = officerIdx >= 0 && officerIdx < startCell.colors.length
          ? startCell.colors[officerIdx]
          : '#ffffff';

        let text: string;
        if (usesCellText) {
          // Officer-schedule mode: show cell text (counter number label) directly
          text = startCell.text ?? '';
        } else {
          const meta = gridData.officer_metadata[seg.officerId];
          const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
          if (seg.officerId.startsWith('OT')) {
            text = seg.officerId.split('_')[0];
          } else if (seg.officerId.startsWith('SN')) {
            // Main officer: show full name if stored, else fall back to the SN number
            const snNum = seg.officerId.slice(2);
            text = meta?.name || snNum;
          } else if (meta) {
            const depAbbr = meta.deployment ? abbreviateDeployment(meta.deployment) : '';
            const nameAbbr = meta.name ? abbreviateName(meta.name) : '';
            text = isMobile
              ? (depAbbr || seg.officerId)
              : (depAbbr && nameAbbr ? `${depAbbr}_${nameAbbr}` : depAbbr || nameAbbr || seg.officerId);
          } else {
            text = seg.officerId;
          }
        }

        allSegments.push({
          officerId: seg.officerId,
          counterRow: row,
          startCol: seg.startCol,
          endCol: seg.endCol,
          lane: seg.lane,
          totalLanes: seg.totalLanes,
          color,
          text,
          opacity: startCell.opacity,
          changed: startCell.changed,
          note: startCell.note,
          duty: !!startCell.duty,
          spanStartCol: seg.startCol,
          spanEndCol: seg.endCol,
          suppressLeftBorder: false,
          suppressRightBorder: false,
        });
      }
    }

    // Post-process: suppress borders between adjacent sub-segments of the same officer,
    // but only when adjacent segments share the same text (same counter assignment).
    // Different counter text means a visible border must be drawn between them.
    const segByEndMap = new Map<string, DrawSegment>();
    const segByStartMap = new Map<string, DrawSegment>();
    for (const seg of allSegments) {
      segByEndMap.set(`${seg.officerId}:${seg.counterRow}:${seg.endCol}`, seg);
      segByStartMap.set(`${seg.officerId}:${seg.counterRow}:${seg.startCol}`, seg);
    }
    for (const seg of allSegments) {
      const nextSeg = segByStartMap.get(`${seg.officerId}:${seg.counterRow}:${seg.endCol + 1}`);
      seg.suppressRightBorder = nextSeg !== undefined && nextSeg.text === seg.text;
      const prevSeg = segByEndMap.get(`${seg.officerId}:${seg.counterRow}:${seg.startCol - 1}`);
      seg.suppressLeftBorder  = prevSeg !== undefined && prevSeg.text === seg.text;
      // An overlap piece (shared cell, so a fraction of the row) beside the same officer's
      // full-height piece is one L-shaped cell: the full-height piece says it all.
      const fullBeside = (o: DrawSegment | undefined) => o !== undefined && o.totalLanes === 1 && o.text === seg.text;
      seg.continuation = seg.totalLanes > 1 && (fullBeside(nextSeg) || fullBeside(prevSeg));
    }
    // The span of each merged shape: walk the edge-to-edge neighbours (same officer, same label).
    for (const seg of allSegments) {
      let end = seg;
      for (let n = segByStartMap.get(`${seg.officerId}:${seg.counterRow}:${end.endCol + 1}`); n && n.text === seg.text; n = segByStartMap.get(`${seg.officerId}:${seg.counterRow}:${end.endCol + 1}`)) end = n;
      let start = seg;
      for (let p = segByEndMap.get(`${seg.officerId}:${seg.counterRow}:${start.startCol - 1}`); p && p.text === seg.text; p = segByEndMap.get(`${seg.officerId}:${seg.counterRow}:${start.startCol - 1}`)) start = p;
      seg.spanStartCol = start.startCol;
      seg.spanEndCol = end.endCol;
    }

    return allSegments;
  }

  /**
   * Resize canvas to fit grid
   */
  private resizeCanvas(gridData: RosterGridData): void {
    const width =
      this.config.labelWidth +
      gridData.num_slots * this.config.cellWidth;
    const height =
      this.config.labelHeight +
      this.totalGridH();

    // Store logical dimensions for clearRect and DPR listener
    this.logicalWidth = width;
    this.logicalHeight = height;

    // Scale physical pixels for high-DPI / browser zoom
    // At a fractional ratio (Windows 125% / 150% display scaling, browser zoom 110%…)
    // width*dpr is rarely a whole number. A bitmap that is not an exact whole number of
    // device pixels gets resampled by the browser, which blurs the text inside the cells
    // while ordinary HTML text stays crisp. So the bitmap is sized in whole device pixels
    // first, the CSS box is derived back from it, and the drawing scale uses the exact
    // bitmap/logical ratio, so one logical px always lands on the same device pixels.
    // iOS Safari refuses canvases past ~16.7M pixels and kills the page ("A problem repeatedly
    // occurred") when canvas memory runs out, which a wide grid at 3x easily reaches. Lower the
    // ratio for such a grid rather than the grid: it just renders a little softer.
    const MAX_BITMAP_PIXELS = 8_000_000;
    const rawDpr = window.devicePixelRatio || 1;
    const dpr = Math.min(rawDpr, Math.max(1, Math.sqrt(MAX_BITMAP_PIXELS / Math.max(1, width * height))));
    const bitmapW = Math.max(1, Math.round(width * dpr));
    const bitmapH = Math.max(1, Math.round(height * dpr));
    // Only when the size changed: assigning width/height, even the same value, throws
    // the bitmap away and allocates a new one — on every redraw, for every edit.
    // render() clears the bitmap itself.
    if (this.canvas.width !== bitmapW || this.canvas.height !== bitmapH) {
      this.canvas.style.width = `${bitmapW / dpr}px`;
      this.canvas.style.height = `${bitmapH / dpr}px`;
      this.canvas.width = bitmapW;
      this.canvas.height = bitmapH;
    }
    // A kept bitmap keeps its context state too: start every redraw from the defaults
    // a fresh one would have had.
    this.ctx.globalAlpha = 1;
    this.ctx.globalCompositeOperation = 'source-over';
    // setTransform replaces the transform outright, so this is safe to call on every
    // render without accumulation.
    this.ctx.setTransform(bitmapW / width, 0, 0, bitmapH / height, 0, 0);

    // Configure text rendering
    this.setFont(`${this.config.fontSize}px ${this.config.fontFamily}`);
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
  }

  /**
   * Draw time labels (top row)
   */
  private drawTimeLabels(labels: string[]): void {
    this.ctx.fillStyle = '#000';
    this.setFont(`${this.config.fontSize}px ${this.config.fontFamily}`);
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';

    labels.forEach((label, index) => {
      // Only show labels at hourly boundaries (every 4 slots of 15 min each).
      // e.g. slot 0=1000, slot 4=1100, slot 8=1200 — hide 1015, 1030, 1045…
      if (index % 4 !== 0) return;

      const x =
        this.config.labelWidth +
        index * this.config.cellWidth +
        this.config.cellWidth / 2;
      const y = this.config.labelHeight / 2;

      this.ctx.fillText(label, x, y);
    });
  }

  /**
   * Draw counter labels (left column).
   * Supports `\n`-separated two-line labels:
   *   line 0 — primary label (bold, slightly above center)
   *   line 1 — secondary label, e.g. "1200–1700" (smaller, muted, below center)
   */
  private drawCounterLabels(labels: string[]): void {
    labels.forEach((label, index) => {
      const x = this.config.labelWidth / 2;
      const yBase = this.rowY(index);
      const rh = this.rowH(index);

      const lines = label.split('\n');
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';

      if (lines.length >= 2) {
        this.ctx.fillStyle = '#000';
        this.setFont(`bold ${this.config.fontSize}px ${this.config.fontFamily}`);
        this.ctx.fillText(lines[0], x, yBase + rh * 0.33);

        this.ctx.fillStyle = '#000';
        this.setFont(`${Math.max(this.config.fontSize - 2, 8)}px ${this.config.fontFamily}`);
        this.ctx.fillText(lines[1], x, yBase + rh * 0.72);
      } else {
        this.ctx.fillStyle = '#000';
        this.setFont(`${this.config.fontSize}px ${this.config.fontFamily}`);
        this.ctx.fillText(label, x, yBase + rh / 2);
      }
    });
  }

  /**
   * Draw complete grid
   */
  private drawGrid(gridData: RosterGridData): void {
    // First pass: draw all cells (background/border for empty and unavailable cells)
    for (const cell of gridData.cells) {
      if ((cell.officers.length === 0 || cell.unavailable) && this.inRows(cell.row)) {
        this.drawCell(cell);
      }
    }

    // Hourly gridlines — drawn after background cells so they show through empty cells
    // but are covered by officer segment fills in the next pass.
    this.drawHourlyGridLines(gridData);
    this.drawLiveNowLine();

    // Second pass: draw officer segments (merged, with lane-based heights)
    if (this.config.mergeConsecutive) {
      for (const seg of this.drawSegments) {
        if (this.inRows(seg.counterRow)) this.drawSegment(seg);
      }
      // Draw L-shape transition borders at segment junctions where lane/height changes
      this.drawSegmentTransitions();
    } else {
      // No merging — draw individual cells that have officers
      for (const cell of gridData.cells) {
        if (cell.officers.length > 0 && !cell.unavailable && this.inRows(cell.row)) {
          this.drawCell(cell);
        }
      }
    }

    // Third pass: bold red hatch over cells that are unavailable AND occupied —
    // signals to the user that assigned officers need to be moved.
    for (const cell of gridData.cells) {
      if (cell.unavailable && cell.officers.length > 0 && this.inRows(cell.row)) {
        this.drawUnavailableHatch(cell);
      }
    }

    // Live presence (other users' cell borders + avatar bubbles) — drawn before
    // brackets so brackets always paint on top and stay visible (see drawPresenceOverlay).
    this.drawPresenceOverlay();

    // Availability brackets — colored diagonal markers at the edges of each
    // officer's availability window, drawn on top of segment fills/borders.
    this.drawAvailabilityBrackets();

    // Redraw segment text on top of brackets — a bracket can cross right through
    // a segment's start/end time annotation (both live at the top corners of the
    // cell), and the annotation reads better than the marker underneath it.
    if (this.config.mergeConsecutive) {
      for (const seg of this.drawSegments) {
        if (this.inRows(seg.counterRow)) this.drawSegmentText(seg);
      }
    }

    // Notes on unassigned/break cells, last: every cell has painted its own
    // background by now, so nothing is left to fill over them.
    this.drawUnassignedNoteCaptions(gridData);
  }

  /**
   * Draw colored < > chevron shapes at the start and end of each officer's
   * availability window. Each chevron is a diagonal line stretched across the
   * full width and height of its cell — a "<" apex on the left side of the
   * start cell, a ">" apex on the right side of the end cell — so the marker
   * reads as a line drawn through the slot rather than a trace along its
   * border. Drawn last so they always appear on top of segment fills.
   */
  private drawAvailabilityBrackets(): void {
    if (!this.gridData?.availability_brackets?.length) return;
    for (const bracket of this.gridData.availability_brackets) {
      if (this.inRows(bracket.row)) this.drawSingleBracket(bracket);
    }
  }

  /**
   * Draw < and/or > chevron marks. showStart/showEnd default to true.
   *
   * config.narrowChevrons (the counter graph) shrinks each chevron to half its
   * cell's width — the "<" occupies just the first half of the start slot, the
   * ">" just the last half of the end slot — which reads more cleanly there
   * than a full-cell chevron. The officer graph leaves this off and spans the
   * full cell, per its own earlier design.
   */
  private drawSingleBracket(bracket: AvailabilityBracket): void {
    const { labelWidth, cellWidth } = this.config;
    const y = this.rowY(bracket.row);
    const cellHeight = this.rowH(bracket.row);
    const yMid = y + cellHeight / 2;
    const showStart = bracket.showStart !== false;
    const showEnd   = bracket.showEnd   !== false;
    if (!showStart && !showEnd) return;
    const chevronW = this.config.narrowChevrons ? cellWidth / 2 : cellWidth;

    this.ctx.save();
    this.ctx.strokeStyle = bracket.color;
    this.ctx.lineWidth = 1.25;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';

    if (showStart) {
      // "<" — apex at the left-middle of the start cell, opening to the right.
      const x1  = labelWidth + bracket.startCol * cellWidth;
      const x1r = x1 + chevronW;
      this.ctx.beginPath();
      this.ctx.moveTo(x1r, y);
      this.ctx.lineTo(x1,  yMid);
      this.ctx.lineTo(x1r, y + cellHeight);
      this.ctx.stroke();
    }

    if (showEnd) {
      // ">" — apex at the right-middle of the end cell, opening to the left.
      const x2  = labelWidth + (bracket.endCol + 1) * cellWidth;
      const x2l = x2 - chevronW;
      this.ctx.beginPath();
      this.ctx.moveTo(x2l, y);
      this.ctx.lineTo(x2,  yMid);
      this.ctx.lineTo(x2l, y + cellHeight);
      this.ctx.stroke();
    }

    this.restoreCtx();
  }

  /**
   * Redraw hourly gridlines and zone-separator lines within a single row's y-strip.
   * Called after partial row repaints (drag highlight, overlay, pending clear) to
   * restore lines that were erased by the row-level clearRect.
   */
  private redrawRowOverlays(row: number): void {
    if (!this.gridData) return;
    const { labelWidth, cellWidth } = this.config;
    const numSlots = this.gridData.num_slots;
    const y = this.rowY(row);
    const rh = this.rowH(row);
    const xEnd = labelWidth + numSlots * cellWidth;

    // Hourly gridlines within this row's y strip.
    // Only draw where the line isn't fully covered by officer fills on both sides.
    this.ctx.save();
    this.ctx.strokeStyle = 'rgba(0,0,0,0.30)';
    this.ctx.lineWidth = 1.5;
    for (let col = 0; col < numSlots; col += 4) {
      const cellAtCol = col < numSlots ? this.gridData.cells[row * numSlots + col] : null;
      const cellBefore = col > 0 ? this.gridData.cells[row * numSlots + (col - 1)] : null;
      const colHasOfficer = !!(cellAtCol && cellAtCol.officers.length > 0 && !cellAtCol.unavailable);
      const beforeHasOfficer = !!(cellBefore && cellBefore.officers.length > 0 && !cellBefore.unavailable);
      if (colHasOfficer && beforeHasOfficer) continue;
      const x = labelWidth + col * cellWidth;
      this.ctx.beginPath();
      this.ctx.moveTo(x, y);
      this.ctx.lineTo(x, y + rh);
      this.ctx.stroke();
    }
    this.restoreCtx();

    // Zone separators touching the top or bottom edge of this row
    const boundaries = this.config.zoneBoundaryRows ?? [];
    const touches = boundaries.filter(b => b === row || b === row + 1);
    if (touches.length > 0) {
      this.ctx.save();
      this.ctx.strokeStyle = '#000';
      this.ctx.lineWidth = 3;
      for (const b of touches) {
        const by = this.rowY(b);
        this.ctx.beginPath();
        this.ctx.moveTo(0, by);
        this.ctx.lineTo(xEnd, by);
        this.ctx.stroke();
      }
      this.restoreCtx();
    }

    // Presence for this row — before brackets, same ordering invariant as elsewhere.
    if (this.presenceEntries.some(p => p.row === row)) {
      this.drawPresenceOverlay(row);
    }

    // Availability brackets for this row
    if (this.gridData.availability_brackets) {
      for (const bracket of this.gridData.availability_brackets) {
        if (bracket.row === row) this.drawSingleBracket(bracket);
      }
    }

    // Re-top this row's segment text over any bracket that crossed it — see
    // the matching call in drawGrid().
    if (this.config.mergeConsecutive) {
      for (const seg of this.drawSegments) {
        if (seg.counterRow === row) this.drawSegmentText(seg);
      }
    }
  }

  /** Draw thick vertical lines at every 4th column (hourly boundary) on the background layer. */
  private drawHourlyGridLines(gridData: RosterGridData): void {
    const { labelWidth, labelHeight, cellWidth } = this.config;
    const gridH = this.totalGridH();
    this.ctx.save();
    this.ctx.strokeStyle = 'rgba(0,0,0,0.30)';
    this.ctx.lineWidth = 1.5;
    for (let col = 0; col < gridData.num_slots; col += 4) {
      const x = labelWidth + col * cellWidth;
      this.ctx.beginPath();
      this.ctx.moveTo(x, labelHeight);
      this.ctx.lineTo(x, labelHeight + gridH);
      this.ctx.stroke();
    }
    this.restoreCtx();
  }

  /**
   * Draw the missing "step" border lines at junctions where adjacent same-officer
   * sub-segments change lane/height (L-shape rendering).
   *
   * When seg ends at col C (suppressing its right border) and next starts at C+1
   * (suppressing its left border), the overlap in y-extent is invisible. But the
   * non-overlapping portions need a vertical line at the transition x to close the L.
   */
  private drawSegmentTransitions(): void {
    const byStart = new Map<string, DrawSegment>();
    for (const seg of this.drawSegments) {
      byStart.set(`${seg.officerId}:${seg.counterRow}:${seg.startCol}`, seg);
    }
    // Assigned/colored segments always get a black border — only empty/unavailable
    // cells use the lighter config.borderColor, so filled cells stand out.
    this.ctx.strokeStyle = '#000';
    this.ctx.lineWidth = this.config.borderWidth;
    for (const seg of this.drawSegments) {
      if (!seg.suppressRightBorder) continue;
      const next = byStart.get(`${seg.officerId}:${seg.counterRow}:${seg.endCol + 1}`);
      if (!next) continue;
      const y = this.rowY(seg.counterRow);
      const transX = this.config.labelWidth + (seg.endCol + 1) * this.config.cellWidth;
      const rh = this.rowH(seg.counterRow);
      const segH = rh / seg.totalLanes;
      const nextH = rh / next.totalLanes;
      const segTop = y + seg.lane * segH;
      const segBottom = segTop + segH;
      const nextTop = y + next.lane * nextH;
      const nextBottom = nextTop + nextH;
      // Draw top step (where one segment starts above the other)
      if (Math.abs(segTop - nextTop) > 0.5) {
        this.ctx.beginPath();
        this.ctx.moveTo(transX, Math.min(segTop, nextTop));
        this.ctx.lineTo(transX, Math.max(segTop, nextTop));
        this.ctx.stroke();
      }
      // Draw bottom step (where one segment ends below the other)
      if (Math.abs(segBottom - nextBottom) > 0.5) {
        this.ctx.beginPath();
        this.ctx.moveTo(transX, Math.min(segBottom, nextBottom));
        this.ctx.lineTo(transX, Math.max(segBottom, nextBottom));
        this.ctx.stroke();
      }
    }
  }

  /** Draw bold red diagonal hatch lines only — overlaid on occupied+unavailable cells. */
  private drawUnavailableHatch(cell: CellData): void {
    const x = this.config.labelWidth + cell.col * this.config.cellWidth;
    const y = this.rowY(cell.row);
    const w = this.config.cellWidth;
    const h = this.rowH(cell.row);
    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.rect(x, y, w, h);
    this.ctx.clip();
    this.ctx.strokeStyle = '#ef4444';
    this.ctx.lineWidth = 2;
    const step = 7;
    for (let d = -h; d < w + h; d += step) {
      this.ctx.beginPath();
      this.ctx.moveTo(x + d, y);
      this.ctx.lineTo(x + d + h, y + h);
      this.ctx.stroke();
    }
    this.restoreCtx();
  }

  private applyHighlightOverlay(): void {
    if (!this.gridData || !this.highlightedOfficerIds || this.highlightedOfficerIds.size === 0) return;
    const { labelWidth, labelHeight, cellWidth } = this.config;
    const numSlots = this.gridData.num_slots;

    // Step 1: dim the entire grid uniformly. A dark overlay (rather than a white wash)
    // gives a reliable luminance drop even against the pale pastel officer-cell fills,
    // so it stays legible under bright ambient light/screen glare.
    this.ctx.save();
    this.ctx.fillStyle = 'rgba(15,23,42,0.55)';
    this.ctx.fillRect(labelWidth, labelHeight, numSlots * cellWidth, this.totalGridH());
    this.restoreCtx();

    // Step 2: re-draw only matching segments at full brightness, on top of the dim,
    // exactly as they render normally — same fill, same border, no extra outline —
    // so matched cells look identical to the un-searched grid, just undimmed.
    for (const seg of this.drawSegments) {
      if (this.highlightedOfficerIds.has(seg.officerId)) {
        this.drawSegment(seg);
      }
    }

    // Step 3: restore zone separators, presence, and availability brackets so none of
    // them are faded or covered by the matched-segment redraw above. Presence goes
    // before brackets, same ordering invariant as the main render() path.
    this.drawZoneSeparators();
    this.drawPresenceOverlay();
    this.drawAvailabilityBrackets();
    this.drawAvailabilityLines();

    // Re-top the matched segments' own text over any bracket that crossed them.
    for (const seg of this.drawSegments) {
      if (this.highlightedOfficerIds.has(seg.officerId)) {
        this.drawSegmentText(seg);
      }
    }
  }

  /**
   * Full-height vertical lines at the start and end of each highlighted officer's
   * availability windows, across every counter row — so while one officer is selected
   * (and the rest of the grid is dimmed) you can see which counters fall inside their
   * window. A white halo under the coloured core keeps them readable on both the dimmed
   * and the undimmed cells. Unlike the < > chevrons, these are not tied to the counter
   * the officer currently stands on.
   */
  private drawAvailabilityLines(): void {
    if (!this.gridData || this.availabilityLines.length === 0) return;
    const { labelWidth, labelHeight, cellWidth } = this.config;
    const top = labelHeight;
    const bottom = labelHeight + this.totalGridH();
    const gridRight = labelWidth + this.gridData.num_slots * cellWidth;
    this.ctx.save();
    this.ctx.lineCap = 'butt';
    for (const line of this.availabilityLines) {
      for (const col of [line.startCol, line.endCol + 1]) {
        // Keep the line inside the grid: a window that starts/ends at the shift edge sits on the border.
        const x = Math.min(gridRight - 1.5, Math.max(labelWidth + 1.5, labelWidth + col * cellWidth));
        this.ctx.beginPath();
        this.ctx.moveTo(x, top);
        this.ctx.lineTo(x, bottom);
        this.ctx.strokeStyle = '#ffffff';
        this.ctx.lineWidth = 5;
        this.ctx.stroke();
        this.ctx.strokeStyle = line.color;
        this.ctx.lineWidth = 2.5;
        this.ctx.stroke();
      }
    }
    this.restoreCtx();
  }

  /**
   * Draw zone separator lines (thick horizontal lines between zone boundary rows).
   */
  private drawZoneSeparators(): void {
    if (!this.gridData || !this.config.zoneBoundaryRows?.length) return;

    this.ctx.save();
    this.ctx.strokeStyle = ZONE_SEPARATOR_COLOR;
    this.ctx.lineWidth = 3;

    for (const boundaryRow of this.config.zoneBoundaryRows) {
      // Draw line at the bottom of boundaryRow - 1 (between row boundaryRow-1 and boundaryRow)
      const y = this.rowY(boundaryRow);
      const xStart = 0;
      const xEnd = this.config.labelWidth + this.gridData.num_slots * this.config.cellWidth;
      this.ctx.beginPath();
      this.ctx.moveTo(xStart, y);
      this.ctx.lineTo(xEnd, y);
      this.ctx.stroke();
    }

    this.restoreCtx();
  }

  /**
   * Draw single cell (used for empty/unavailable cells and non-merged mode)
   */
  drawCell(cell: CellData): void {
    const x = this.config.labelWidth + cell.col * this.config.cellWidth;
    const y = this.rowY(cell.row);
    const w = this.config.cellWidth;
    const h = this.rowH(cell.row);

    const dimmed = cell.opacity !== undefined && cell.opacity < 1;
    if (dimmed) { this.ctx.save(); this.ctx.globalAlpha = cell.opacity!; }

    // Fill background
    this.ctx.fillStyle = cell.color;
    this.ctx.fillRect(x, y, w, h);

    // Diagonal hatch for unavailable cells (matches Plotly style)
    if (cell.unavailable) {
      this.ctx.save();
      this.ctx.beginPath();
      this.ctx.rect(x, y, w, h);
      this.ctx.clip();
      this.ctx.strokeStyle = '#bbb';
      this.ctx.lineWidth = 1;
      const step = 6;
      for (let d = -h; d < w + h; d += step) {
        this.ctx.beginPath();
        this.ctx.moveTo(x + d, y);
        this.ctx.lineTo(x + d + h, y + h);
        this.ctx.stroke();
      }
      this.restoreCtx();
    }

    // Draw border
    this.ctx.strokeStyle = this.config.borderColor;
    this.ctx.lineWidth = this.config.borderWidth;
    this.ctx.strokeRect(x, y, w, h);

    // Draw cell text when present.
    // Break cells (no officers, text="#" or "#\nHHMM") show only the # symbol —
    // any embedded time annotation is intentionally ignored so break rows stay clean.
    if (cell.text) {
      const mainText = cell.text.split('\n')[0];
      const isBreakCell = cell.officers.length === 0 && mainText === '#';
      const ink = cell.unavailable ? '#000' : isBreakCell ? '#3B6D11' : '#000';

      this.ctx.fillStyle = ink;
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';
      const label = this.fitLabel(mainText, w - 2, h, this.config.fontSize);
      if (label) {
        this.setFont(`bold ${label.fontSize}px ${this.config.fontFamily}`);
        this.drawLabelLines(label.lines, x + w / 2, y, h);
      }
    }

    // A note on a cell no officer segment covers — a break or an unassigned/green
    // slot (assigned cells get their caption from drawSegmentText instead, since
    // they're drawn as merged segments, not one drawCell() per slot). Drawn once
    // per contiguous same-note run so a multi-slot note gets one wide pill instead
    // of a tiny one repeated every 15 minutes.
    if (dimmed) this.restoreCtx();
  }

  /**
   * Captions for notes on cells no officer segment covers — a break or an
   * unassigned/green slot. (Assigned cells get theirs from drawSegmentText, which
   * paints a whole merged segment at once.)
   *
   * This has to be its own pass, run after every cell has filled its background:
   * each cell paints only its own column, so a caption spanning a multi-slot run
   * drawn from inside drawCell() is immediately erased by the next cell in that
   * run filling over it — leaving at most a sliver in the first column.
   *
   * One caption per contiguous same-note run, so a note covering two hours gets a
   * single wide pill rather than one repeated every 15 minutes. `onlyRow` limits
   * the pass to a single row, for the partial row-repaint path.
   */
  private drawUnassignedNoteCaptions(gridData: RosterGridData, onlyRow?: number): void {
    const numSlots = gridData.num_slots;
    const annSize = annotationFontSize(this.config.fontSize, ANNOTATION_MIN_PX);
    for (let row = 0; row < gridData.num_counters; row++) {
      if ((onlyRow !== undefined && row !== onlyRow) || !this.inRows(row)) continue;
      let col = 0;
      while (col < numSlots) {
        const cell = gridData.cells[row * numSlots + col];
        if (!cell?.note || cell.officers.length > 0) { col++; continue; }
        let endCol = col;
        while (endCol + 1 < numSlots) {
          const next = gridData.cells[row * numSlots + endCol + 1];
          if (!next || next.note !== cell.note || next.officers.length > 0) break;
          endCol++;
        }
        // No label shares an empty cell, so the caption sits mid-row.
        const rowTop = this.rowY(row);
        const capX = this.config.labelWidth + col * this.config.cellWidth;
        const capW = (endCol - col + 1) * this.config.cellWidth;
        const drew = this.drawNoteCaption(
          cell.note, capX, rowTop, capW, 0, 0, annSize,
          rowTop + Math.max(1, (this.rowH(row) - annSize) / 2),
        );
        if (!drew) this.drawNoteFlag(capX, rowTop, capW, this.rowH(row));
        col = endCol + 1;
      }
    }
  }

  /** Paint stacked label lines, evenly spaced down the given band (font already set). */
  private drawLabelLines(lines: string[], cx: number, top: number, height: number): void {
    if (lines.length === 1) { this.ctx.fillText(lines[0], cx, top + height / 2); return; }
    // Two lines sit at 30% / 70%; more are spread over the same band's centre.
    const step = lines.length === 2 ? height * 0.4 : height / lines.length;
    const first = top + height / 2 - (step * (lines.length - 1)) / 2;
    lines.forEach((l, i) => this.ctx.fillText(l, cx, first + step * i));
  }

  /** Fit one cell's label to the space it has — see fitCellLabel. */
  private fitLabel(
    text: string,
    maxWidth: number,
    maxHeight: number,
    fontSize: number,
    isDuty = false,
  ): FittedLabel | null {
    return fitCellLabel(text, {
      maxWidth,
      maxHeight,
      fontSize,
      // Names may be shortened, and so may a deployment's text; counter labels may not.
      mayTruncate: isDuty || this.config.textMode !== 'cell_text',
      measure: (s, size) => {
        this.setFont(`bold ${size}px ${this.config.fontFamily}`);
        return this.textWidth(s);
      },
    });
  }

  /**
   * Draw one officer's merged segment, respecting lane/totalLanes for fractional height.
   */
  drawSegment(seg: DrawSegment): void {
    const x = this.config.labelWidth + seg.startCol * this.config.cellWidth;
    const segHeight = this.rowH(seg.counterRow) / seg.totalLanes;
    const y = this.rowY(seg.counterRow) + seg.lane * segHeight;
    const width = (seg.endCol - seg.startCol + 1) * this.config.cellWidth;

    const dimmed = seg.opacity !== undefined && seg.opacity < 1;
    this.ctx.save();
    this.ctx.globalAlpha = dimmed ? seg.opacity! : 1;

    // Fill background
    this.ctx.fillStyle = seg.color;
    this.ctx.fillRect(x, y, width, segHeight);

    // Draw border — suppress shared edges between adjacent same-officer sub-segments.
    // Always black for an assigned segment, unlike the gray config.borderColor used
    // for empty/unavailable cells, so filled cells stand out and empty ones recede.
    this.ctx.strokeStyle = '#000';
    this.ctx.lineWidth = this.config.borderWidth;
    this.ctx.beginPath();
    this.ctx.moveTo(x, y); this.ctx.lineTo(x + width, y);                              // top
    if (!seg.suppressRightBorder) { this.ctx.moveTo(x + width, y); this.ctx.lineTo(x + width, y + segHeight); } // right
    this.ctx.moveTo(x + width, y + segHeight); this.ctx.lineTo(x, y + segHeight);       // bottom
    if (!seg.suppressLeftBorder)  { this.ctx.moveTo(x, y + segHeight); this.ctx.lineTo(x, y); }                 // left
    this.ctx.stroke();

    this.restoreCtx();

    this.drawSegmentText(seg);
  }

  /**
   * Draw a segment's text label and time annotations only (no fill/border) —
   * split out of drawSegment() so it can be redrawn a second time, after
   * availability brackets, wherever a bracket might otherwise cross over and
   * partially cover the start/end time annotations. Recomputes the same
   * geometry drawSegment() used for its fill/border.
   */
  private drawSegmentText(seg: DrawSegment): void {
    if (!seg.text || !this.gridData || seg.continuation) return;

    const x = this.config.labelWidth + seg.startCol * this.config.cellWidth;
    const fullHeight = this.rowH(seg.counterRow) / seg.totalLanes;
    const y = this.rowY(seg.counterRow) + seg.lane * fullHeight;
    const width = (seg.endCol - seg.startCol + 1) * this.config.cellWidth;

    // A row with a note lane keeps the bottom line of every single-lane cell for it, so the label
    // and times of all of them sit at the same height; a note too narrow for its cell goes there.
    const laneLines = this.laneTracks.get(seg.counterRow) ?? 0;
    const inLaneRow = laneLines > 0 && this.laneLineH > 0;
    // The lane is the bottom of the row; only the bottom block of a stack overlaps it.
    const bottomBlock = seg.lane === seg.totalLanes - 1;
    const segHeight = inLaneRow && bottomBlock ? Math.max(0, fullHeight - laneLines * this.laneLineH) : fullHeight;
    const noteToLane = inLaneRow && needsNoteLane(seg.note);
    const drawn: DrawSegment = noteToLane ? { ...seg, note: undefined } : seg;

    // The text starts clear of a phone icon sitting in the segment's top-left corner.
    const inset = Math.min(this.textInsets.get(`${seg.officerId}|${seg.counterRow}|${seg.startCol}`) ?? 0, width / 2);
    // ...and clear of an NR button at its right edge: the text is centred in what is left.
    const rightInset = Math.min(this.rightInsets.get(`${seg.officerId}|${seg.counterRow}|${seg.startCol}`) ?? 0, (width - inset) / 2);

    const spanSlots = seg.endCol - seg.startCol + 1;
    // Times only if: single-lane AND span > 3 slots (otherwise cell is too narrow)
    const showTimes = seg.totalLanes === 1 && spanSlots > 3;
    // End time only if: times enabled AND next cell on same row is empty
    const nextCell = seg.spanEndCol + 1 < this.gridData.num_slots
      ? this.gridData.cells[seg.counterRow * this.gridData.num_slots + seg.spanEndCol + 1]
      : null;
    const showEndTime = showTimes && (!nextCell || nextCell.officers.length === 0);
    const labelSize = this.config.fontSize;
    const annSize = annotationFontSize(labelSize, ANNOTATION_MIN_PX);
    // A main officer's assignment here differs from the initially-generated roster
    // (counter and/or start/end time) — flag it in red rather than the usual black.
    const ink = this.callPending(seg) ? '#dc2626' : '#000';
    const timeFont = `500 ${annSize}px ${this.config.fontFamily}`;
    // An L-shaped cell reads as one block: its times are those of the whole shape.
    const startLabel = this.gridData.time_labels[seg.spanStartCol];
    const endLabel = this.slotEndTime(seg.spanEndCol);

    this.ctx.save();
    if (seg.opacity !== undefined && seg.opacity < 1) this.ctx.globalAlpha = seg.opacity;
    this.ctx.beginPath();
    this.ctx.rect(x, y, width, segHeight);
    this.ctx.clip();

    // Times above the name wherever the row is tall enough to hold both (every row the
    // grid sizes is); a row squeezed below that falls back to one line.
    const stacked = fitsStacked(segHeight, annSize, labelSize);
    if (stacked) {
      this.drawStackedText(drawn, x + inset, y, width - inset - rightInset, segHeight, {
        annSize, labelSize, ink, timeFont, showTimes, showEndTime, startLabel, endLabel,
      });
    } else {
      this.drawInlineText(drawn, x + inset, y, width - inset - rightInset, segHeight, {
        annSize, labelSize, ink, timeFont, showTimes, showEndTime, startLabel, endLabel,
      });
    }

    this.restoreCtx();

    if (noteToLane) {
      this.drawLaneNote(seg, this.rowY(seg.counterRow) + this.rowH(seg.counterRow) - laneLines * this.laneLineH, annSize);
    }
  }

  /**
   * A narrow cell's note, in the lane along the bottom of its row. The pill starts under its cell
   * and reaches sideways over the lane beside it as far as its text needs, on a second line where
   * another note is in the way (see layoutLaneTracks); with no free line at all it falls back to
   * the corner flag.
   */
  private drawLaneNote(seg: DrawSegment, laneTop: number, annSize: number): void {
    if (!this.gridData || !seg.note) return;
    const { labelWidth, cellWidth } = this.config;
    const widthOf = (o: DrawSegment) => (o.endCol - o.startCol + 1) * cellWidth;
    const inLane = this.drawSegments
      .filter((o) => o.counterRow === seg.counterRow && !o.continuation && needsNoteLane(o.note))
      .sort((a, b) => a.startCol - b.startCol);
    const at = inLane.indexOf(seg);
    // The pill has to fit the line that was reserved, whatever size the font has since become.
    const fontSize = Math.max(1, Math.min(annSize, this.laneLineH - 3));
    this.setFont(`bold ${fontSize}px ${this.config.fontFamily}`);
    const { pills } = layoutLaneTracks(
      inLane.map((o) => ({ x: labelWidth + o.startCol * cellWidth, w: widthOf(o), note: o.note ?? '' })),
      labelWidth + this.gridData.num_slots * cellWidth,
      (t) => this.textWidth(t),
      { rowLeft: labelWidth },
    );
    const pill = at >= 0 ? pills[at] : null;
    if (!pill) {
      this.drawNoteFlag(labelWidth + seg.startCol * cellWidth, this.rowY(seg.counterRow), widthOf(seg), this.rowH(seg.counterRow));
      return;
    }
    this.drawNotePill(pill.text, pill.x, laneTop + pill.track * this.laneLineH + 1, pill.width, fontSize);
  }

  /** Rows tall enough for two lines: times and note on top, label below (see segmentLayout). */
  private drawStackedText(
    seg: DrawSegment, x: number, y: number, width: number, segHeight: number,
    t: SegmentTextStyle,
  ): void {
    let topRowHeight = 0;
    let startLabelWidth = 0;
    let endLabelWidth = 0;

    if (t.showTimes) {
      this.setFont(t.timeFont);
      const fits = fitTimes(
        width, this.textWidth(t.startLabel), t.showEndTime ? this.textWidth(t.endLabel) : 0,
      );
      this.ctx.fillStyle = t.ink;
      this.ctx.textBaseline = 'top';
      if (fits.showStart) {
        this.ctx.textAlign = 'left';
        this.ctx.fillText(t.startLabel, x + 2, y + 1);
        startLabelWidth = this.textWidth(t.startLabel);
      }
      if (fits.showEnd) {
        this.ctx.textAlign = 'right';
        this.ctx.fillText(t.endLabel, x + width - 2, y + 1);
        endLabelWidth = this.textWidth(t.endLabel);
      }
      if (fits.showStart) topRowHeight = t.annSize + 2;
    }

    // Note caption on that same top line, between the times. A cell too narrow for
    // the HHMM labels still gets one (there are none to avoid, so it has the width).
    if (seg.note) {
      const drew = this.drawNoteCaption(seg.note, x, y, width, startLabelWidth, endLabelWidth, t.annSize);
      if (drew) topRowHeight = Math.max(topRowHeight, t.annSize + 2);
      else this.drawNoteFlag(x, y, width, segHeight);
    }

    // Counter/officer label, laid out to whatever space is left below — split over
    // two lines, or dropped entirely, rather than cut short.
    this.ctx.fillStyle = t.ink;
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    const labelTop = y + topRowHeight;
    const labelHeight = segHeight - topRowHeight;
    const label = this.fitLabel(seg.text, width - 4, labelHeight, t.labelSize, seg.duty);
    if (label) {
      this.setFont(`bold ${label.fontSize}px ${this.config.fontFamily}`);
      this.drawLabelLines(label.lines, x + width / 2, labelTop, labelHeight);
    }
  }

  /** Compact rows: everything on one line — [start] [note…] label [end] (see segmentLayout). */
  private drawInlineText(
    seg: DrawSegment, x: number, y: number, width: number, segHeight: number,
    t: SegmentTextStyle,
  ): void {
    this.setFont(t.timeFont);
    const startWidth = t.showTimes ? this.textWidth(t.startLabel) : 0;
    const endWidth = t.showEndTime ? this.textWidth(t.endLabel) : 0;

    const fit = (maxWidth: number) => this.fitLabel(seg.text, maxWidth, segHeight, t.labelSize, seg.duty);
    const labelWidthOf = (label: FittedLabel) => {
      this.setFont(`bold ${label.fontSize}px ${this.config.fontFamily}`);
      return Math.max(...label.lines.map((l) => this.textWidth(l)));
    };
    const noteFont = `bold ${t.annSize}px ${this.config.fontFamily}`;
    const layout = layoutInline({
      x, width, startWidth, endWidth, note: seg.note,
      fitLabel: (maxWidth) => { const l = fit(maxWidth); return l ? labelWidthOf(l) : null; },
      measureNote: (str) => { this.setFont(noteFont); return this.textWidth(str) + 4; },
    });
    const midY = y + segHeight / 2;

    this.ctx.fillStyle = t.ink;
    this.setFont(t.timeFont);
    this.ctx.textBaseline = 'middle';
    if (layout.showStart) {
      this.ctx.textAlign = 'left';
      this.ctx.fillText(t.startLabel, x + 2, midY);
    }
    if (layout.showEnd) {
      this.ctx.textAlign = 'right';
      this.ctx.fillText(t.endLabel, x + width - 2, midY);
    }
    if (layout.note) {
      this.drawNotePill(layout.note.text, layout.note.x, midY - t.annSize / 2, layout.note.width, t.annSize);
    } else if (seg.note) {
      // No room beside the times and label: a saved note must not be invisible.
      this.drawNoteFlag(x, y, width, segHeight);
    }
    const label = layout.labelMaxWidth !== null ? fit(layout.labelMaxWidth) : null;
    if (label && layout.labelCx !== null) {
      this.ctx.fillStyle = t.ink;
      this.setFont(`bold ${label.fontSize}px ${this.config.fontFamily}`);
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';
      this.drawLabelLines(label.lines, layout.labelCx, y, segHeight);
    }
  }

  /**
   * Draw a segment's note as a "tiktok caption" — bold black text on a light
   * rounded pill — centered on the segment's top line.
   *
   * `startLabelWidth`/`endLabelWidth` are the room the HHMM labels have already
   * taken on that line (0 when they aren't drawn); the caption gets what's left,
   * measured rather than estimated so it can never overlap them. A cell too
   * narrow for even one truncated character still gets a small empty pill (no
   * text) rather than nothing — the user at least sees a note is saved there,
   * and can open the officer card to read it in full.
   *
   * Returns whether anything was drawn, so the caller knows whether the top line
   * is spoken for.
   */
  private drawNoteCaption(
    note: string,
    x: number,
    y: number,
    width: number,
    startLabelWidth: number,
    endLabelWidth: number,
    fontSize: number,
    pillTop?: number,
  ): boolean {
    const padX = 2;
    const sideMargin = 2;
    const MIN_EMPTY_PILL_WIDTH = 6;

    const availLeft = x + 1 + (startLabelWidth > 0 ? startLabelWidth + sideMargin : 0);
    const availRight = x + width - 1 - (endLabelWidth > 0 ? endLabelWidth + sideMargin : 0);
    const availWidth = availRight - availLeft;
    if (availWidth < MIN_EMPTY_PILL_WIDTH) return false;

    this.setFont(`bold ${fontSize}px ${this.config.fontFamily}`);
    const noteText = ellipsize(note, availWidth - padX * 2, (t) => this.textWidth(t));
    // No character fits — still show a small empty pill so a saved note is
    // never invisible, just unreadable at this width.
    const pillWidth = noteText
      ? Math.min(this.textWidth(noteText) + padX * 2, availWidth)
      : Math.min(MIN_EMPTY_PILL_WIDTH, availWidth);
    const pillX = Math.max(availLeft, Math.min(x + width / 2 - pillWidth / 2, availRight - pillWidth));
    this.drawNotePill(noteText, pillX, pillTop ?? y + 1, pillWidth, fontSize);
    return true;
  }

  /** A small corner flag saying a note is saved on this cell — see noteFlag. Drawn on top of the
   *  cell's text and inside its clip, so it never moves anything. */
  private drawNoteFlag(x: number, y: number, width: number, height: number): void {
    const corners = noteFlag(x, y, width, height);
    if (!corners) return;
    this.ctx.beginPath();
    this.ctx.moveTo(corners[0][0], corners[0][1]);
    this.ctx.lineTo(corners[1][0], corners[1][1]);
    this.ctx.lineTo(corners[2][0], corners[2][1]);
    this.ctx.closePath();
    this.ctx.fillStyle = '#f59e0b';
    this.ctx.fill();
    this.ctx.strokeStyle = '#000';
    this.ctx.lineWidth = 0.5;
    this.ctx.stroke();
  }

  /** Paint one note caption: bold black text on a light rounded pill. Empty text draws
   *  just the pill — a marker that a note is saved there, readable on hover. */
  private drawNotePill(text: string, pillX: number, pillY: number, pillWidth: number, fontSize: number): void {
    const pillHeight = fontSize + 1;
    this.drawRoundedRectPath(pillX, pillY, pillWidth, pillHeight, Math.min(pillHeight / 2, 4));
    this.ctx.fillStyle = 'rgba(255,255,255,0.92)';
    this.ctx.fill();
    // A thin border so the white fill reads as a distinct caption against a
    // cell that may itself be light-colored, rather than blending into it.
    this.ctx.strokeStyle = '#000';
    this.ctx.lineWidth = 0.5;
    this.ctx.stroke();
    if (text) {
      this.ctx.fillStyle = '#000';
      this.setFont(`bold ${fontSize}px ${this.config.fontFamily}`);
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'top';
      this.ctx.fillText(text, pillX + pillWidth / 2, pillY + 1);
    }
  }

  /** Traces a rounded-rectangle path on the current context (caller fills/strokes). */
  private drawRoundedRectPath(x: number, y: number, w: number, h: number, r: number): void {
    const radius = Math.max(0, Math.min(r, w / 2, h / 2));
    this.ctx.beginPath();
    this.ctx.moveTo(x + radius, y);
    this.ctx.arcTo(x + w, y, x + w, y + h, radius);
    this.ctx.arcTo(x + w, y + h, x, y + h, radius);
    this.ctx.arcTo(x, y + h, x, y, radius);
    this.ctx.arcTo(x, y, x + w, y, radius);
    this.ctx.closePath();
  }

  /**
   * Returns the end time label for a slot (start of next slot, or +15 min for last).
   */
  private slotEndTime(col: number): string {
    if (col + 1 < this.gridData!.num_slots) {
      return this.gridData!.time_labels[col + 1];
    }
    // Last slot: add 15 minutes to its start time
    const label = this.gridData!.time_labels[col];
    const h = parseInt(label.substring(0, 2));
    const m = parseInt(label.substring(2, 4));
    const totalMin = h * 60 + m + 15;
    return `${Math.floor(totalMin / 60).toString().padStart(2, '0')}${(totalMin % 60).toString().padStart(2, '0')}`;
  }

  /**
   * Shared hit-detection logic: given canvas-space (x, y) coordinates,
   * returns the HoverInfo for the cell/officer under the cursor, or null if out of bounds.
   */
  private getHoverInfoAt(x: number, y: number): HoverInfo | null {
    if (!this.gridData) return null;

    // Convert to grid coordinates
    const col = Math.floor((x - this.config.labelWidth) / this.config.cellWidth);
    const row = this.rowFromY(y);

    // Check bounds
    if (
      col < 0 ||
      col >= this.gridData.num_slots ||
      row < 0 ||
      row >= this.gridData.num_counters
    ) {
      return null;
    }

    const cellIndex = row * this.gridData.num_slots + col;
    const cell = this.gridData.cells[cellIndex];

    // Determine which lane is being hovered (for fractional-height overlap rendering)
    const yWithinRow = y - this.rowY(row);
    const fracWithinRow = yWithinRow / this.rowH(row); // 0..1

    let hoveredOfficer: string | undefined;
    let hoveredNote: string | undefined;
    let hoveredText: string | undefined;
    let hoveredColor: string | undefined;
    let timeLabel = this.gridData.time_labels[col];

    if (this.config.mergeConsecutive) {
      for (const seg of this.drawSegments) {
        if (
          seg.counterRow === row &&
          seg.startCol <= col &&
          col <= seg.endCol
        ) {
          const laneStart = seg.lane / seg.totalLanes;
          const laneEnd = (seg.lane + 1) / seg.totalLanes;
          if (fracWithinRow >= laneStart && fracWithinRow < laneEnd) {
            hoveredOfficer = seg.officerId;
            hoveredNote = seg.note;
            hoveredText = seg.text;
            hoveredColor = seg.color;
            const startTime = this.gridData.time_labels[seg.spanStartCol];
            const endTime = this.slotEndTime(seg.spanEndCol);
            timeLabel = `${startTime}-${endTime}`;
            break;
          }
        }
      }
      // Fallback: use first officer if no lane matched
      if (!hoveredOfficer && cell.officers.length > 0) {
        hoveredOfficer = cell.officers[0];
      }
    } else {
      hoveredOfficer = cell.officers[0];
    }

    // For overlap slots, populate the officers[] array with full time-range info per officer
    const multiOfficers = cell.officers.length > 1
      ? cell.officers.map((id) => {
          let minCol = col;
          let maxCol = col;
          for (let c = col - 1; c >= 0; c--) {
            if (this.gridData!.cells[row * this.gridData!.num_slots + c].officers.includes(id)) minCol = c;
            else break;
          }
          for (let c = col + 1; c < this.gridData!.num_slots; c++) {
            if (this.gridData!.cells[row * this.gridData!.num_slots + c].officers.includes(id)) maxCol = c;
            else break;
          }
          const tl = this.gridData!.time_labels;
          return {
            id,
            metadata: this.gridData!.officer_metadata[id],
            timeRange: `${tl[minCol]}-${this.slotEndTime(maxCol)}`,
            counter: this.gridData!.counter_labels[row],
          };
        })
      : undefined;

    return {
      row,
      col,
      officer: hoveredOfficer,
      metadata: hoveredOfficer
        ? this.gridData.officer_metadata[hoveredOfficer]
        : undefined,
      officers: multiOfficers,
      time: timeLabel,
      counter: this.gridData.counter_labels[row],
      blockText: hoveredText,
      color: hoveredColor,
      // Notes are cut to fit their block, so the hover card is where they are read whole.
      note: hoveredNote ?? cell.note,
    };
  }

  /**
   * Determine cell type for drag mode auto-detection and auto-stop at type boundaries.
   * 'counter' — has an officer assignment; 'break' — break marker (#); 'empty' — unassigned.
   */
  private getCellType(row: number, col: number): 'counter' | 'break' | 'empty' {
    if (!this.gridData) return 'empty';
    const cell = this.gridData.cells[row * this.gridData.num_slots + col];
    if (!cell) return 'empty';
    if (cell.officers.length > 0) return 'counter';
    if (cell.text != null && cell.text.length > 0) return 'break';
    return 'empty';
  }

  /**
   * Walk from startCol toward targetCol and return the last column that has the
   * same cell type as startCellType (contiguous run, stops at type boundary).
   */
  private clampToTypeBoundary(
    row: number,
    startCol: number,
    targetCol: number,
    startCellType: 'counter' | 'break' | 'empty',
  ): number {
    const dir = targetCol >= startCol ? 1 : -1;
    let validEnd = startCol;
    for (
      let c = startCol + dir;
      dir === 1 ? c <= targetCol : c >= targetCol;
      c += dir
    ) {
      if (this.getCellType(row, c) !== startCellType) break;
      validEnd = c;
    }
    return validEnd;
  }

  /**
   * Handle mouse move: update hover info, or paint drag highlight if dragging.
   */
  private handleMouseMove = (event: MouseEvent): void => {
    const rect = this.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    this.lastPointer = { x: event.clientX, y: event.clientY };

    if (this.blockDrag) return; // the owner draws the drag UI; no hover tooltip meanwhile
    if (this.dragState) {
      if (this.mouseDownPt && !this.dragState.blockRuledOut) {
        const dx = Math.abs(event.clientX - this.mouseDownPt.x);
        const dy = Math.abs(event.clientY - this.mouseDownPt.y);
        // The axis is decided once, the first time the pointer leaves the
        // threshold (before or after a hold): mostly vertical off an officer
        // block = pick it up; anything else rules a move out for this press, so
        // a sideways erase-range drag can't turn into a move halfway through.
        if (dx > this.BLOCK_DRAG_THRESHOLD_PX || dy > this.BLOCK_DRAG_THRESHOLD_PX) {
          const vertical = dy > dx && this.blockDragEnabled && this.dragState.startCellType === 'counter';
          if (vertical && this.beginBlockDrag(event.clientX, event.clientY)) return;
          this.dragState.blockRuledOut = true;
        }
      }
      const col = Math.floor((x - this.config.labelWidth) / this.config.cellWidth);
      const row = this.rowFromY(y);
      if (
        this.gridData &&
        col >= 0 &&
        col < this.gridData.num_slots &&
        row === this.dragState.row
      ) {
        // Clamp to contiguous run of same cell type
        const validEnd = this.clampToTypeBoundary(row, this.dragState.startCol, col, this.dragState.startCellType);
        if (validEnd !== this.dragState.curCol) {
          this.dragState.curCol = validEnd;
          const { row: r, startCol, curCol, startCellType } = this.dragState;
          const mode: 'add' | 'erase' = startCellType === 'empty' ? 'add' : 'erase';
          this.paintDragHighlight(r, Math.min(startCol, curCol), Math.max(startCol, curCol), mode);
        }
      }
      return; // suppress hover tooltip while dragging
    }

    this.emitHover(this.getHoverInfoAt(x, y));
  };

  /**
   * Report what is under the pointer — but only when that changes. mousemove fires
   * dozens of times a second while the pointer crosses one cell, and every report is a
   * state update that re-renders the whole grid component around this canvas.
   */
  private emitHover(info: HoverInfo | null): void {
    const key = info ? `${info.row}:${info.col}:${info.officer ?? ''}:${info.time}` : '';
    if (key === this.lastHoverKey && this.gridData === this.lastHoverData) return;
    this.lastHoverKey = key;
    this.lastHoverData = this.gridData;
    this.hoverCallback?.(info);
  }

  /**
   * Start tracking a potential drag or click.
   */
  private handleMouseDown = (event: MouseEvent): void => {
    if (event.button !== 0) return; // left button only
    const rect = this.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const col = Math.floor((x - this.config.labelWidth) / this.config.cellWidth);
    const row = this.rowFromY(y);
    if (
      !this.gridData ||
      col < 0 || col >= this.gridData.num_slots ||
      row < 0 || row >= this.gridData.num_counters
    ) return;
    const startCellType = this.getCellType(row, col);
    this.dragState = { row, startCol: col, curCol: col, startCellType };
    this.mouseDownPt = { x: event.clientX, y: event.clientY };
    document.addEventListener('mouseup', this.handleMouseUp);

    if (!this.longPressRowPredicate || this.longPressRowPredicate(row)) {
      const mode: 'add' | 'erase' = startCellType === 'empty' ? 'add' : 'erase';
      this.mouseLongPressTimer = setTimeout(() => {
        this.mouseLongPressTimer = null;
        // Bail if the button was already released, moved to a different row/
        // column (a real drag, not a hold), or this is a stale timer from a
        // press that's already been handled.
        if (!this.dragState || this.dragState.row !== row || this.dragState.startCol !== col) return;
        if (this.dragState.curCol !== col) return;
        // Like a touch long-press: show the selection now, but report it on
        // release — until then the press can still become a range (sideways)
        // or a block move (up/down).
        this.dragState.longPressFired = true;
        this.paintDragHighlight(row, col, col, mode);
      }, this.MOUSE_LONG_PRESS_MS);
    }
  };

  /**
   * Resolve drag as either a click (same cell) or a drag-select (multi-cell).
   */
  private handleMouseUp = (event: MouseEvent): void => {
    document.removeEventListener('mouseup', this.handleMouseUp);
    this.mouseDownPt = null;
    if (this.mouseLongPressTimer !== null) {
      clearTimeout(this.mouseLongPressTimer);
      this.mouseLongPressTimer = null;
    }
    if (!this.dragState) return;

    const { row, startCol, curCol, startCellType, longPressFired } = this.dragState;
    const startC = Math.min(startCol, curCol);
    const endC = Math.max(startCol, curCol);
    this.dragState = null;
    const mode: 'add' | 'erase' = startCellType === 'empty' ? 'add' : 'erase';
    // A hold (with or without a sideways drag after it) is reported on release,
    // exactly like a touch long-press.
    if (longPressFired) {
      this.onDragSelectCallback?.(row, startC, endC, event.clientX, event.clientY, mode, 'drag');
      return;
    }

    if (startC === endC) {
      const rect = this.canvas.getBoundingClientRect();
      const upX = event.clientX - rect.left;
      const upY = event.clientY - rect.top;

      // Presence avatar bubble takes priority over the normal cell click — clicking
      // it reveals who's editing rather than opening that cell's own edit popup.
      const hitBubble = this.presenceBubbleHitboxes.find(b => Math.hypot(b.x - upX, b.y - upY) <= b.r);
      if (hitBubble) {
        this.presenceBubbleClickCallback?.(hitBubble.userName, event.clientX, event.clientY);
        return;
      }

      // Single click: if the cell has an assigned officer, open the edit popup.
      // For empty/break cells, open the assign popover (single-slot add/erase).
      const info = this.getHoverInfoAt(upX, upY);
      if (info?.officer) {
        info.clientX = event.clientX;
        info.clientY = event.clientY;
        this.clickCallback?.(info);
      } else {
        this.onDragSelectCallback?.(row, startC, endC, event.clientX, event.clientY, mode, 'click');
      }
    } else {
      // Multi-cell drag — fire drag-select callback with add/erase mode
      this.onDragSelectCallback?.(row, startC, endC, event.clientX, event.clientY, mode, 'drag');
    }
  };

  /**
   * Handle mouse leave
   */
  private handleMouseLeave = (): void => {
    this.lastPointer = null;
    this.emitHover(null);
  };

  /** The mouse's last position over the canvas (viewport coordinates); null once it has left. */
  private lastPointer: { x: number; y: number } | null = null;

  /**
   * The grid scrolled (wheel, trackpad, scrollbar) under a pointer that did not move: browsers send no
   * mousemove for that, so the hover card kept naming the cell that WAS under the pointer — a row off
   * from the label beside it. Re-read what is under the pointer now. During a block drag the same
   * applies to the counter being aimed at.
   */
  refreshPointer(): void {
    if (this.blockDrag) { this.reevaluateBlockDrag(); return; }
    const p = this.lastPointer;
    if (!p || this.dragState) return;
    const rect = this.canvas.getBoundingClientRect();
    const x = p.x - rect.left;
    const y = p.y - rect.top;
    const inside = x >= 0 && y >= 0 && x <= rect.width && y <= rect.height;
    this.emitHover(inside ? this.getHoverInfoAt(x, y) : null);
  }

  // ── Touch long-press handlers ──────────────────────────────────────────────

  private handleTouchStart = (event: TouchEvent): void => {
    if (event.touches.length !== 1) return;
    const touch = event.touches[0];
    const rect = this.canvas.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    const col = Math.floor((x - this.config.labelWidth) / this.config.cellWidth);
    const row = this.rowFromY(y);
    if (
      !this.gridData ||
      col < 0 || col >= this.gridData.num_slots ||
      row < 0 || row >= this.gridData.num_counters
    ) return;

    this.touchState = 'pending';
    this.touchStart = { clientX: touch.clientX, clientY: touch.clientY, row, col, startCellType: this.getCellType(row, col) };
    this.touchCurCol = col;
    this.touchBlockRuledOut = false;

    // No immediate press feedback: a plain press/tap on an empty cell must be fully
    // inert — it must not tint the cell or touch the hourly gridline / zone-separator
    // lines. A deliberate long-press upgrades to the drag-selection highlight below
    // (which repaints the row cleanly), and a quick tap just fires the click callback.

    // After LONG_PRESS_MS with no horizontal scroll intent, enter selection mode
    this.touchLongPressTimer = setTimeout(() => {
      if (this.touchState === 'pending') {
        this.touchState = 'selecting';
        // Upgrade pending highlight to a proper drag highlight
        const { row: r, col: startC, startCellType } = this.touchStart!;
        const mode: 'add' | 'erase' = startCellType === 'empty' ? 'add' : 'erase';
        this.paintDragHighlight(r, startC, this.touchCurCol, mode);
      }
    }, this.LONG_PRESS_MS);
  };

  private handleTouchMove = (event: TouchEvent): void => {
    if (!this.touchStart || event.touches.length !== 1) return;
    const touch = event.touches[0];
    const deltaX = Math.abs(touch.clientX - this.touchStart.clientX);
    const deltaY = Math.abs(touch.clientY - this.touchStart.clientY);

    if (this.touchState === 'pending') {
      if (deltaX > this.SCROLL_THRESHOLD_PX || deltaY > this.SCROLL_THRESHOLD_PX) {
        // Any scroll-like movement before long-press fires — cancel and let browser handle
        this.cancelTouchSelection();
      }
      // Else: still pending, do nothing (let browser also receive the event for scroll)
      return;
    }

    if (this.touchState === 'blockDragging') {
      event.preventDefault();
      this.emitBlockDrag('move', touch.clientX, touch.clientY);
      return;
    }

    if (this.touchState === 'selecting') {
      // We own this gesture — suppress scroll and update the highlight
      event.preventDefault();
      if (!this.touchBlockRuledOut && (deltaX > this.BLOCK_DRAG_THRESHOLD_PX || deltaY > this.BLOCK_DRAG_THRESHOLD_PX)) {
        // Held long enough to rule out a scroll; the first real movement decides
        // the axis for the rest of the press. Up/down on an officer block picks
        // it up (near the screen edge the owner auto-scrolls, and a drop only
        // proposes a move — confirm bar). Sideways locks into the erase range.
        const vertical = deltaY > deltaX && this.blockDragEnabled && this.touchStart.startCellType === 'counter';
        if (vertical && this.beginBlockDrag(touch.clientX, touch.clientY, this.touchStart.row, this.touchStart.col)) {
          this.touchState = 'blockDragging';
          try { navigator.vibrate?.(15); } catch { /* not supported */ }
          return;
        }
        this.touchBlockRuledOut = true;
      }
      const rect = this.canvas.getBoundingClientRect();
      const col = Math.floor(
        (touch.clientX - rect.left - this.config.labelWidth) / this.config.cellWidth,
      );
      if (this.gridData && col >= 0 && col < this.gridData.num_slots) {
        const { row, col: startCol, startCellType } = this.touchStart;
        // Clamp to contiguous run of same cell type (auto-stop at boundary)
        const validEnd = this.clampToTypeBoundary(row, startCol, col, startCellType);
        this.touchCurCol = validEnd;
        const mode: 'add' | 'erase' = startCellType === 'empty' ? 'add' : 'erase';
        this.paintDragHighlight(row, Math.min(startCol, validEnd), Math.max(startCol, validEnd), mode);
      }
    }
  };

  private handleTouchEnd = (event: TouchEvent): void => {
    if (!this.touchStart) return;
    if (this.touchLongPressTimer !== null) {
      clearTimeout(this.touchLongPressTimer);
      this.touchLongPressTimer = null;
    }

    if (this.touchState === 'blockDragging') {
      // Same reasoning as the 'selecting' branch: swallow the synthesized click.
      event.preventDefault();
      const t = event.changedTouches[0];
      this.endBlockDrag('end', t.clientX, t.clientY);
      this.touchState = 'idle';
      this.touchStart = null;
      return;
    }

    if (this.touchState === 'selecting') {
      // Without this, Android/iOS follow up a long-press-and-lift with synthesized
      // mousedown/mouseup/click events at the same point ~afterward. Any row opted
      // into mouse long-press (setLongPressRowPredicate) would then see a SECOND,
      // unwanted 'click' gesture fire right after this real one — e.g. popping the
      // officer-edit popup open over/instead of the split/add button this touch
      // was supposed to raise. Same protection the 'pending' tap branch below
      // already relies on.
      event.preventDefault();
      const { row, col: startCol, startCellType } = this.touchStart;
      const startC = Math.min(startCol, this.touchCurCol);
      const endC = Math.max(startCol, this.touchCurCol);
      // Use changedTouches for position of the lifted finger
      const t = event.changedTouches[0];
      // Single-cell long-press fires drag-select (allows single-slot add/erase)
      const mode: 'add' | 'erase' = startCellType === 'empty' ? 'add' : 'erase';
      this.onDragSelectCallback?.(row, startC, endC, t.clientX, t.clientY, mode, 'drag');
    } else {
      // Was pending or scrolling — treat as a tap if state is pending and finger barely moved
      if (this.touchState === 'pending') {
        const t = event.changedTouches[0];
        const rect = this.canvas.getBoundingClientRect();
        const info = this.getHoverInfoAt(t.clientX - rect.left, t.clientY - rect.top);
        if (info) {
          // Prevent browser from synthesizing mousedown/mouseup/click after this touchend.
          // Without this, the synthetic click lands on the popup that just opened,
          // causing an immediate accidental second interaction.
          event.preventDefault();
          info.clientX = t.clientX;
          info.clientY = t.clientY;
          this.clickCallback?.(info);
        }
      }
    }

    this.touchState = 'idle';
    this.touchStart = null;
  };

  private handleTouchCancel = (): void => {
    if (this.touchState === 'blockDragging') this.endBlockDrag('cancel');
    this.cancelTouchSelection();
    this.touchState = 'idle';
    this.touchStart = null;
  };

  /** Cancel a pending long-press: clear timer and let scroll proceed. */
  private cancelTouchSelection(): void {
    if (this.touchLongPressTimer !== null) {
      clearTimeout(this.touchLongPressTimer);
      this.touchLongPressTimer = null;
    }
    this.touchState = 'scrolling';
  }

  // ── Drag-to-move of an officer block ───────────────────────────────────────

  /** Register the owner of block drags; null (or enabled=false) switches the gesture off. */
  setBlockDragHandler(handler: ((e: BlockDragEvent) => void) | null, enabled = true): void {
    this.blockDragHandler = handler;
    this.blockDragEnabled = enabled && handler !== null;
    if (!this.blockDragEnabled && this.blockDrag) this.endBlockDrag('cancel');
  }

  /** Row under a viewport point, or -1 over a zone gap / outside the grid vertically. */
  private rowAtClientY(clientY: number): number {
    const rect = this.canvas.getBoundingClientRect();
    const y = clientY - rect.top;
    if (y < 0 || y > this.totalGridH()) return -1;
    return this.rowFromY(y);
  }

  private emitBlockDrag(phase: BlockDragEvent['phase'], clientX: number, clientY: number): void {
    const d = this.blockDrag;
    if (!d) return;
    d.lastX = clientX;
    d.lastY = clientY;
    this.blockDragHandler?.({
      phase,
      block: d.block,
      sourceRow: d.sourceRow,
      row: phase === 'cancel' ? -1 : this.rowAtClientY(clientY),
      clientX,
      clientY,
    });
  }

  /** Re-report the last pointer position — the grid scrolled under a still pointer. */
  reevaluateBlockDrag(): void {
    if (this.blockDrag) this.emitBlockDrag('move', this.blockDrag.lastX, this.blockDrag.lastY);
  }

  /** Abort a drag in progress (e.g. Escape). Reports 'cancel'. */
  cancelBlockDrag(): void {
    if (this.blockDrag) this.endBlockDrag('cancel');
  }

  /**
   * Turn the current press into a block drag. Mouse presses use the tracked
   * dragState; touch passes the long-pressed cell explicitly.
   */
  private beginBlockDrag(clientX: number, clientY: number, row?: number, col?: number): boolean {
    if (!this.gridData || !this.blockDragHandler) return false;
    const r = row ?? this.dragState?.row;
    const c = col ?? this.dragState?.startCol;
    if (r === undefined || c === undefined) return false;
    // Which officer was pressed matters in a shared (overlapping) cell: the lane
    // under the press point says which one. Fall back to the sole occupant.
    const press = row === undefined ? this.mouseDownPt : this.touchStart;
    let pressed: string | undefined;
    if (press) {
      const rect = this.canvas.getBoundingClientRect();
      const px = 'clientX' in press ? press.clientX : press.x;
      const py = 'clientY' in press ? press.clientY : press.y;
      pressed = this.getHoverInfoAt(px - rect.left, py - rect.top)?.officer;
    }
    const block = findOfficerBlock(this.gridData.cells, this.gridData.num_slots, r, c, pressed);
    if (!block) return false;

    if (this.mouseLongPressTimer !== null) {
      clearTimeout(this.mouseLongPressTimer);
      this.mouseLongPressTimer = null;
    }
    const isMouse = row === undefined;
    if (isMouse) {
      this.dragState = null;
      this.mouseDownPt = null;
      document.removeEventListener('mouseup', this.handleMouseUp);
      document.addEventListener('mousemove', this.handleBlockMouseMove);
      document.addEventListener('mouseup', this.handleBlockMouseUp);
    }
    // Wipe whatever selection tint a long-press painted; the owner draws the drag UI.
    this.repaintRow(r);
    this.blockDrag = { block, sourceRow: r, lastX: clientX, lastY: clientY };
    this.emitHover(null);
    document.addEventListener('keydown', this.handleBlockKeyDown);
    this.emitBlockDrag('start', clientX, clientY);
    // Report where the pointer already is, so the counter under it is highlighted at once
    // rather than after the next movement.
    this.emitBlockDrag('move', clientX, clientY);
    return true;
  }

  private endBlockDrag(phase: 'end' | 'cancel', clientX?: number, clientY?: number): void {
    document.removeEventListener('mousemove', this.handleBlockMouseMove);
    document.removeEventListener('mouseup', this.handleBlockMouseUp);
    document.removeEventListener('keydown', this.handleBlockKeyDown);
    if (!this.blockDrag) return;
    this.emitBlockDrag(phase, clientX ?? this.blockDrag.lastX, clientY ?? this.blockDrag.lastY);
    this.blockDrag = null;
  }

  private handleBlockMouseMove = (event: MouseEvent): void => {
    this.emitBlockDrag('move', event.clientX, event.clientY);
  };

  private handleBlockMouseUp = (event: MouseEvent): void => {
    this.endBlockDrag('end', event.clientX, event.clientY);
  };

  private handleBlockKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') this.endBlockDrag('cancel');
  };

  /** Redraw one row from gridData, dropping any selection tint (gridlines included). */
  private repaintRow(row: number): void {
    if (!this.gridData) return;
    // The same passes as render(), so the canvas still shows exactly what was last
    // drawn and the next render may repaint just the rows an edit changes.
    if (this.lastDrawn?.gridData === this.gridData) {
      this.repaintRows(this.gridData, new Set([row]));
      this.repainted = null;
      return;
    }
    this.restoreRowPixels(row);
    this.redrawRowOverlays(row);
  }

  private restoreRowPixels(row: number): void {
    if (!this.gridData) return;
    this.paintedOutsideRender();
    const { labelWidth, cellWidth } = this.config;
    const rh = this.rowH(row);
    const y = this.rowY(row);
    const numSlots = this.gridData.num_slots;

    this.ctx.clearRect(labelWidth, y, numSlots * cellWidth, rh);
    for (let col = 0; col < numSlots; col++) {
      const cell = this.gridData.cells[row * numSlots + col];
      if (cell.officers.length === 0 || cell.unavailable) this.drawCell(cell);
    }
    for (const seg of this.drawSegments) {
      if (seg.counterRow === row) this.drawSegment(seg);
    }
    // Restore red hatch for occupied+unavailable cells
    for (let col = 0; col < numSlots; col++) {
      const cell = this.gridData.cells[row * numSlots + col];
      if (cell.unavailable && cell.officers.length > 0) this.drawUnavailableHatch(cell);
    }
  }

  /**
   * Paint a drag-selection highlight on a single officer row.
   * Called on every mousemove tick during drag for immediate visual feedback.
   * The highlight persists (as painted pixels) until the next full render() call.
   */
  paintDragHighlight(row: number, startCol: number, endCol: number, mode: 'add' | 'erase' = 'add'): void {
    if (!this.gridData) return;
    const { labelWidth, cellWidth } = this.config;
    const rh = this.rowH(row);
    const y = this.rowY(row);

    this.restoreRowPixels(row);

    // Selection tint — blue for add, red for erase
    const tintColor = mode === 'erase' ? 'rgba(220,38,38,0.28)' : 'rgba(37,99,235,0.18)';
    const borderColor = mode === 'erase' ? '#dc2626' : '#2563eb';
    const s = Math.min(startCol, endCol);
    const e = Math.max(startCol, endCol);
    for (let col = s; col <= e; col++) {
      this.ctx.fillStyle = tintColor;
      this.ctx.fillRect(labelWidth + col * cellWidth, y, cellWidth, rh);
    }

    // Selection border
    const sx = labelWidth + s * cellWidth;
    const w = (e - s + 1) * cellWidth;
    this.ctx.strokeStyle = borderColor;
    this.ctx.lineWidth = 2;
    this.ctx.strokeRect(sx + 1, y + 1, w - 2, rh - 2);

    // Restore hourly gridlines and zone separators erased by the row clearRect
    this.redrawRowOverlays(row);
  }

  /**
   * Watch for browser zoom changes (which change devicePixelRatio) and re-render
   * to keep canvas sharp. Uses matchMedia once-listener pattern: when the current
   * DPR no longer matches, re-render and register a new listener for the new DPR.
   */
  private setupDprListener(): void {
    // Clean up any previous listener first
    this.dprCleanup?.();

    const dpr = window.devicePixelRatio || 1;
    const mql = window.matchMedia(`(resolution: ${dpr}dppx)`);
    const handler = () => {
      if (this.gridData) this.render(this.gridData);
      this.setupDprListener(); // re-register for next zoom change
    };
    mql.addEventListener('change', handler, { once: true });
    // The resolution query can miss a fractional ratio (e.g. 1.25 reported as 1.2500000298),
    // so a window resize — which browser zoom also fires — re-checks the ratio directly.
    const onResize = () => {
      if ((window.devicePixelRatio || 1) !== dpr) handler();
    };
    window.addEventListener('resize', onResize);
    this.dprCleanup = () => {
      mql.removeEventListener('change', handler);
      window.removeEventListener('resize', onResize);
    };
  }

  /**
   * Cleanup
   */
  /** Expose current rendering config (cell/label dimensions) for overlay positioning. */
  public getConfig(): Readonly<RenderConfig> {
    return this.config;
  }

  /** The call blocks still owed a call, as ranges per officer (row null = any row, for the officer grid). */
  private pendingCalls: Array<{ officerId: string; row: number | null; start: number; end: number }> = [];

  /**
   * Which blocks still have a call owed. Their text is drawn red for exactly as long as their
   * phone icon is up — ticking the icon (the officer was told) clears both together.
   */
  setPendingCalls(calls: Array<{ officerId: string; row: number | null; start: number; end: number }>): void {
    const same = calls.length === this.pendingCalls.length && calls.every((c, i) => {
      const p = this.pendingCalls[i];
      return p.officerId === c.officerId && p.row === c.row && p.start === c.start && p.end === c.end;
    });
    if (same) return;
    this.pendingCalls = calls;
    if (this.gridData) this.render(this.gridData);
  }

  private callPending(seg: DrawSegment): boolean {
    return this.pendingCalls.some((c) => c.officerId === seg.officerId
      && (c.row === null || c.row === seg.counterRow) && seg.startCol >= c.start && seg.startCol <= c.end);
  }

  /** Keep segment text clear of the overlays pinned to a segment's top-left corner (see textInsets). */
  setTextInsets(insets: Map<string, number>): void {
    const same = insets.size === this.textInsets.size && [...insets].every(([k, v]) => this.textInsets.get(k) === v);
    if (same) return;
    this.textInsets = insets;
    if (this.gridData) this.render(this.gridData);
  }

  /** Keep segment text clear of an NR button pinned to a segment's right edge. */
  setRightInsets(insets: Map<string, number>): void {
    const same = insets.size === this.rightInsets.size && [...insets].every(([k, v]) => this.rightInsets.get(k) === v);
    if (same) return;
    this.rightInsets = insets;
    if (this.gridData) this.render(this.gridData);
  }

  /** Set the availability lines shown over the dimmed grid (see drawAvailabilityLines). */
  setAvailabilityLines(lines: AvailabilityLine[]): void {
    this.availabilityLines = lines;
    if (this.gridData) this.render(this.gridData);
  }

  /**
   * Where each merged officer segment sits, in canvas pixels — for HTML overlays pinned to a
   * block's corner (the phone-call icons). Valid for the last render().
   */
  getSegmentRects(): Array<{ officerId: string; row: number; startCol: number; endCol: number; x: number; y: number; w: number; h: number }> {
    const { labelWidth, cellWidth } = this.config;
    return this.drawSegments.map((seg) => {
      const h = this.rowH(seg.counterRow) / seg.totalLanes;
      return {
        officerId: seg.officerId,
        row: seg.counterRow,
        startCol: seg.startCol,
        endCol: seg.endCol,
        x: labelWidth + seg.startCol * cellWidth,
        y: this.rowY(seg.counterRow) + seg.lane * h,
        w: (seg.endCol - seg.startCol + 1) * cellWidth,
        h,
      };
    });
  }

  setHighlightedOfficers(ids: Set<string> | null): void {
    this.highlightedOfficerIds = ids;
    if (this.gridData) this.render(this.gridData);
  }

  /**
   * Narrow mouse long-press to specific rows (see the mouseLongPressTimer
   * fields above) — pass null to allow it on every row (the default). Safe to call on
   * every render; takes effect on the next mousedown.
   */
  setLongPressRowPredicate(predicate: ((row: number) => boolean) | null): void {
    this.longPressRowPredicate = predicate;
  }

  /**
   * Paint a semi-transparent highlight overlay on cells changed by another user.
   * Called after render() so the highlight sits on top without a full repaint.
   *
   * The canvas transform already has DPR scaling applied via setTransform in
   * resizeCanvas(), so all coordinates here are in logical (CSS) pixels.
   */
  overlayEditTraces(traces: Array<{ row: number; col: number; color: string; traceType?: 'add' | 'delete' | 'change' }>): void {
    // Call after every render(), with an empty list when there are none: after a render
    // that repainted only some rows, the rest still carry the traces drawn before. The
    // same traces again are drawn on the repainted rows only; different ones (or none,
    // where some were) need the grid drawn whole first.
    this.activeTraces = traces ?? [];
    const key = traces?.length ? JSON.stringify(traces) : '';
    if ((this.repainted || this.tracesKey !== '') && key !== this.tracesKey && this.gridData) {
      // Other traces were drawn before (or a partial repaint left them): start from a clean grid.
      // The full redraw puts the (new) active traces back on itself.
      this.lastDrawn = null;
      this.render(this.gridData);
      return;
    }
    // This pass already carries exactly these traces (render() drew them): drawing again would
    // stack the translucent fill.
    if (key === this.tracesKey && this.tracesGen === this.renderGen) return;
    this.tracesKey = key;
    this.tracesGen = this.renderGen;
    if (!traces || traces.length === 0) return;
    const { labelWidth, cellWidth } = this.config;

    // Group by (row, color) and merge into contiguous horizontal segments
    type Segment = { row: number; startCol: number; endCol: number; color: string };
    const segMap = new Map<string, { cols: number[]; color: string; row: number }>();
    for (const { row, col, color } of traces) {
      const key = `${row}:${color}`;
      if (!segMap.has(key)) segMap.set(key, { cols: [], color, row });
      segMap.get(key)!.cols.push(col);
    }

    const segments: Segment[] = [];
    for (const { row, cols, color } of segMap.values()) {
      // After a repaint of some rows, the rest still carry their traces: the fill is
      // translucent, so it is drawn again only inside the repainted strips (clipped below).
      if (this.repainted && !this.repainted.rows.has(row)) continue;
      cols.sort((a, b) => a - b);
      let segStart = cols[0];
      let prev = cols[0];
      for (let i = 1; i < cols.length; i++) {
        if (cols[i] === prev + 1) {
          prev = cols[i];
        } else {
          segments.push({ row, startCol: segStart, endCol: prev, color });
          segStart = cols[i];
          prev = cols[i];
        }
      }
      segments.push({ row, startCol: segStart, endCol: prev, color });
    }

    this.ctx.save();
    if (this.repainted) this.clipToStrips(this.repainted.strips);
    for (const { row, startCol, endCol, color } of segments) {
      const x = labelWidth + startCol * cellWidth;
      const y = this.rowY(row);
      const w = (endCol - startCol + 1) * cellWidth;
      const h = this.rowH(row);
      this.ctx.fillStyle = color + '28';
      this.ctx.fillRect(x, y, w, h);
      this.ctx.strokeStyle = color;
      this.ctx.lineWidth = 2;
      this.ctx.strokeRect(x + 1, y + 1, w - 2, h - 2);
    }
    this.restoreCtx();
  }

  /**
   * Set which cells other users currently have open for editing. Takes effect on the
   * next render()/drawGrid() pass — call before render(), not after (presence is baked
   * into the draw pipeline so it can sit below availability brackets; see drawPresenceOverlay).
   */
  setPresence(entries: Array<{ row: number; col: number; officerKey?: string; color: string; userName: string }>): void {
    this.presenceEntries = entries;
  }

  /** Find the merged draw segment at (row, col) — prefers an exact officerId match
   * (disambiguates overlapping lanes) and falls back to any segment covering the cell. */
  private findSegmentAt(row: number, col: number, officerKey?: string): DrawSegment | undefined {
    let anyMatch: DrawSegment | undefined;
    for (const seg of this.drawSegments) {
      if (seg.counterRow !== row || col < seg.startCol || col > seg.endCol) continue;
      if (officerKey && seg.officerId === officerKey) return seg;
      if (!anyMatch) anyMatch = seg;
    }
    return anyMatch;
  }

  /**
   * Draws a live "someone has this cell open" indicator: a colored border around the
   * FULL merged segment (replacing its default gray border, not just the one clicked
   * slot) plus a small avatar bubble in the corner. Never fills the cell — a border-only
   * treatment so the officer/time text underneath stays fully readable. Must be called
   * before drawAvailabilityBrackets() in every draw path (render/highlight/row-repaint)
   * so brackets always paint on top and stay visible.
   *
   * @param onlyRow — when set, only redraws entries on this row (used by the per-row
   * partial-repaint path so a full-canvas presence redraw isn't needed for a 1-row change).
   */
  private drawPresenceOverlay(onlyRow?: number): void {
    // Full pass clears everything; a row-scoped pass only drops that row's stale
    // hitboxes so repeated partial repaints (e.g. drag highlight) don't accumulate dupes.
    if (onlyRow === undefined) this.presenceBubbleHitboxes = [];
    else this.presenceBubbleHitboxes = this.presenceBubbleHitboxes.filter(b => b.row !== onlyRow);
    if (!this.presenceEntries.length) return;
    const { fontSize, fontFamily } = this.config;

    for (const entry of this.presenceEntries) {
      if (onlyRow !== undefined && entry.row !== onlyRow) continue;

      const seg = this.findSegmentAt(entry.row, entry.col, entry.officerKey);
      let x: number, y: number, width: number, height: number;
      if (seg) {
        x = this.config.labelWidth + seg.startCol * this.config.cellWidth;
        height = this.rowH(seg.counterRow) / seg.totalLanes;
        y = this.rowY(seg.counterRow) + seg.lane * height;
        width = (seg.endCol - seg.startCol + 1) * this.config.cellWidth;
      } else {
        // Fallback: single-cell rect. Shouldn't normally happen — presence is only
        // ever sent for a click on an assigned officer cell, which always has a segment.
        x = this.config.labelWidth + entry.col * this.config.cellWidth;
        y = this.rowY(entry.row);
        width = this.config.cellWidth;
        height = this.rowH(entry.row);
      }

      this.ctx.save();
      this.ctx.strokeStyle = entry.color;
      this.ctx.lineWidth = 2.5;
      this.ctx.strokeRect(x + 1.25, y + 1.25, width - 2.5, height - 2.5);
      this.restoreCtx();

      // Small avatar bubble, Google-Sheets style, pinned to the segment's top-right corner.
      const r = Math.max(6, Math.min(9, Math.round(fontSize * 0.75)));
      const bx = x + width - r - 2;
      const by = y + r + 2;
      this.ctx.save();
      this.ctx.beginPath();
      this.ctx.arc(bx, by, r, 0, Math.PI * 2);
      this.ctx.fillStyle = entry.color;
      this.ctx.fill();
      this.ctx.strokeStyle = '#fff';
      this.ctx.lineWidth = 1.5;
      this.ctx.stroke();

      const parts = entry.userName.trim().split(/\s+/).filter(Boolean);
      const initials = (parts[0]?.[0] ?? '?').toUpperCase() + (parts.length > 1 ? (parts[1][0] ?? '').toUpperCase() : '');
      this.ctx.fillStyle = '#fff';
      this.setFont(`700 ${Math.max(7, r)}px ${fontFamily}`);
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';
      this.ctx.fillText(initials, bx, by + 0.5);
      this.restoreCtx();

      this.presenceBubbleHitboxes.push({ x: bx, y: by, r: r + 2, row: entry.row, userName: entry.userName });
    }
  }

  destroy(): void {
    this.canvas.removeEventListener('mousemove', this.handleMouseMove);
    this.canvas.removeEventListener('mouseleave', this.handleMouseLeave);
    this.canvas.removeEventListener('mousedown', this.handleMouseDown);
    document.removeEventListener('mouseup', this.handleMouseUp);
    document.removeEventListener('mousemove', this.handleBlockMouseMove);
    document.removeEventListener('mouseup', this.handleBlockMouseUp);
    document.removeEventListener('keydown', this.handleBlockKeyDown);
    this.canvas.removeEventListener('touchstart', this.handleTouchStart);
    this.canvas.removeEventListener('touchmove', this.handleTouchMove);
    this.canvas.removeEventListener('touchend', this.handleTouchEnd);
    this.canvas.removeEventListener('touchcancel', this.handleTouchCancel);
    if (this.touchLongPressTimer !== null) clearTimeout(this.touchLongPressTimer);
    if (this.mouseLongPressTimer !== null) clearTimeout(this.mouseLongPressTimer);
    this.dprCleanup?.();

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }
}
